<template>
  <div>
    <div class="row">
      <div class="col-sm-2">
        <OverviewBox title="Order" avatarImage="/glass/img/orders-overview-order3.png" :avatarCircleMode="false" :loading="pageDataLoading">
          <h4>{{orderTitle}}</h4>

          <OverviewBoxInfoItem v-if="order.contact" icon="fa-user-circle">
            <LinkContact :id="order.contactId" :name="order.contact.name" />
          </OverviewBoxInfoItem>

          <OverviewBoxInfoItem icon="fa-map-marker-alt" v-if="isDespatchDelivery">
            <LinkGoogleMap
              :address1="order.deliveryAddress1"
              :address2="order.deliveryAddress2"
              :suburb="order.deliverySuburb"
              :postcode="order.deliveryPostcode"
              :state="order.deliveryState"
              :country="order.deliveryCountry"
              />
          </OverviewBoxInfoItem>
          <OverviewBoxInfoItem icon="fa-repeat" v-if="order.originalOrderId && order.originalOrder">
            <LinkOrder :id="order.originalOrderId" :name="order.originalOrder.sequenceNumber || 'Original Order'" />                        
          </OverviewBoxInfoItem>
          <OverviewBoxInfoItem icon="fa-tag" v-if="order.customerRef">
            {{order.customerRef}}
          </OverviewBoxInfoItem>
          <OverviewBoxInfoItem icon="fa-usd-circle">
            {{order.totalPrice | formatCurrency}}
          </OverviewBoxInfoItem>
          <OverviewBoxInfoItem icon="fa-calendar" v-if="!order.dateRequested">
            {{order.dateCurrentlyDue | formatDate}}
            <span v-if="isStatusDraft"> <Tooltip label="Order is in draft and the estimated date may change when draft is approved"><i class="far fa-exclamation-circle status-order-notice"></i></Tooltip></span>
          </OverviewBoxInfoItem>
          <OverviewBoxInfoItem icon="fa-calendar-exclamation" v-if="order.dateRequested">
            <span class="status-order-notice">{{order.dateRequested | formatDateTime}}</span>
          </OverviewBoxInfoItem>
          
          <OverviewBoxList>
            <OverviewBoxListItem title="Type">
              <LabelBadge :labelClass="`label-order-type-${order.type}`">{{orderTypeTitle}}</LabelBadge>
            </OverviewBoxListItem>
            <OverviewBoxListItem title="Status">
              <LabelOrderStatusBadge :status="order.status" :isUrgent="order.isUrgent" />
            </OverviewBoxListItem>
            <OverviewBoxListItem title="Invoicing" v-if="order.isInvoicingComplete && !isOrderCancelled">
              <span class="label label-order-invoiced"><i class="far fa-fw fa-envelope-open-dollar"></i> Invoicing Completed</span>
            </OverviewBoxListItem>
            <OverviewBoxListItem title="On Hold" v-if="order.isOnHold">
              <LabelOrderStatusBadge :isOnHold="order.isOnHold" :holdType="order.holdType" />
            </OverviewBoxListItem>
            <OverviewBoxListItem title="Despatch" v-if="order.despatchTaskType">
              <LabelBadge labelClass="label-despatch-badge">{{order.despatchTaskType.name}}</LabelBadge>
            </OverviewBoxListItem>
            <OverviewBoxListItem title="Delivery Tag" v-if="addressDeliveryLabelList.length > 0">
              <LabelTags :labelList="addressDeliveryLabelList" />
            </OverviewBoxListItem>
            <OverviewBoxListItem title="Payment Type">
              <LabelBadge :labelClass="`label-credit-type-${order.orderPaymentType}`">{{order.orderPaymentType | formatLookupName(OrderPaymentTypeLookup)}}</LabelBadge>
            </OverviewBoxListItem>
            <OverviewBoxListItem title="Number" v-if="order.sequenceNumber">{{order.sequenceNumber}}</OverviewBoxListItem>
            <OverviewBoxListItem title="Location" v-if="order.accountLocation">{{order.accountLocation.name}}</OverviewBoxListItem>
            <OverviewBoxListItem title="Created By">
              <LinkUser v-if="order.requestUser" :id="order.requestUserId" :name="order.requestUser.name" />
            </OverviewBoxListItem>
            <OverviewBoxListItem title="Created On">{{order.createdAt | formatDate}}</OverviewBoxListItem>
            <OverviewBoxListItem title="Last Updated">{{order.updatedAt | formatDate}}</OverviewBoxListItem>
          </OverviewBoxList>
        </OverviewBox>

      </div>

      <div class="col-sm-10">
        <TabbedBox ref="tabBox" :loading="pageDataLoading">
          <template v-slot:tools>
            
          </template>

          <template v-slot:tablist>
            <Tab :first="true" ref="summaryTab" tabid="tab-order-summary">
              <i class="far fa-tasks"></i> Summary
            </Tab>
            <Tab ref="detailsTab" tabid="tab-order-details" @show="onTabShowDetails">
              <i class="far fa-clipboard-list-check"></i> Details
            </Tab>
            <Tab ref="productionTab" tabid="tab-order-production" v-if="order.requiresProduction && (isOrderStartedProduction || isOrderInPreProduction)">
              <i class="far fa-tools"></i> Production
            </Tab>
            <Tab tabid="tab-order-documents">
              <i class="far fa-book"></i> Documents
            </Tab>
            <Tab tabid="tab-order-ncrlist" v-if="hasLinkedNCROrders">
              <i class="far fa-repeat"></i> NCR Orders
            </Tab>
            <Tab tabid="tab-order-activitylog">
              <i class="far fa-history"></i> Activity Log
            </Tab>
          </template>

          <template v-slot:tabcontent>
            <TabPage :first="true" tabid="tab-order-summary">
              <div class="row">
                <div class="col-lg-12" v-if="order.id">
                  <OrderHoldBar
                    v-if="order.isOnHold"
                    :order="order"
                    :canUnHoldOrder="canUnHoldOrder"
                    :canCancelOrder="canCancelOrder"
                    @unHoldOrder="unHoldOrder"
                    @cancelOrder="cancelOrder" />

                  <ActionDropMenu class="float-right">
                    <DropdownMenuItem v-if="userHasUpdatePermission(PermissionTypes.OrderPermissionTypes.order) && canHoldOrder" @click="holdOrder" icon="fa-pause">Hold Order</DropdownMenuItem>
                    <DropdownMenuItem v-if="userHasUpdatePermission(PermissionTypes.OrderPermissionTypes.order) && canUnHoldOrder" @click="unHoldOrder" icon="fa-play">Un hold Order</DropdownMenuItem>
                    <DropdownMenuItem v-if="userHasUpdatePermission(PermissionTypes.OrderPermissionTypes.orderCancel) && canCancelOrder" @click="cancelOrder" icon="fa-times">Cancel Order</DropdownMenuItem>
                    <DropdownMenuItem v-if="userHasUpdatePermission(PermissionTypes.OrderPermissionTypes.order) && canRestoreOrder" @click="restoreOrder" icon="fa-undo">Restore Order</DropdownMenuItem>
                    <DropdownMenuItem v-if="userHasCreatePermission(PermissionTypes.OrderPermissionTypes.order) && canCreateNCR" @click="createNCR" icon="fa-repeat">Create NCR</DropdownMenuItem>
                    <DropdownMenuItem v-if="userHasUpdatePermission(PermissionTypes.OrderPermissionTypes.order) && canRecheckApprovals" @click="recheckApprovals" icon="fa-sync">Re-check Approvals</DropdownMenuItem>
                    <DropdownMenuItem v-if="userHasUpdatePermission(PermissionTypes.OrderPermissionTypes.order) && canRecheckPurchaseOrders" @click="recheckPurchaseOrders" icon="fa-sync">Re-check Purchase Orders</DropdownMenuItem>
                    <DropdownMenuItem v-if="userHasUpdatePermission(PermissionTypes.OrderPermissionTypes.orderAccountsApproval) && canSkipPrePaid" @click="skipPrePaid" icon="fa-forward">Skip Pre-Paid</DropdownMenuItem>
                    <DropdownMenuItem v-if="userHasUpdatePermission(PermissionTypes.OrderPermissionTypes.orderAccountsApproval) && canSkipCashOnDelivery" @click="skipCashOnDelivery" icon="fa-forward">Skip Cash On Delivery</DropdownMenuItem>
                    <DropdownMenuItem v-if="userHasUpdatePermission(PermissionTypes.OrderPermissionTypes.orderAccountsApproval) && canForceInvoice" @click="forceOrderInvoiced" icon="fa-envelope-open-dollar">Force Order Invoiced</DropdownMenuItem>
                    <DropdownMenuItem v-if="userHasUpdatePermission(PermissionTypes.OrderPermissionTypes.order) && canForceDespatch" @click="forceOrderDespatched" icon="fa-truck">Force Order Despatched</DropdownMenuItem>
                    <DropdownMenuItem v-if="userHasUpdatePermission(PermissionTypes.OrderPermissionTypes.order) && canSendOrder" @click="sendOrder" icon="fa-envelope">Send Order</DropdownMenuItem>

                    <DropdownMenuDivider v-if="(userHasUpdatePermission(PermissionTypes.AdminPermissionTypes.systemAdministrator) && (isOrderInPreProduction || isOrderInProduction || canRecheckOrderProcessing)) || canRevertDespatch || canRecheckOrderProduction" />
                    <DropdownMenuItem v-if="userHasUpdatePermission(PermissionTypes.AdminPermissionTypes.systemAdministrator) && isOrderInPreProduction" @click="sysAdminForceSkipPreProduction" icon="fa-user-shield">Admin: Force Skip Pre Production</DropdownMenuItem>
                    <DropdownMenuItem v-if="userHasUpdatePermission(PermissionTypes.AdminPermissionTypes.systemAdministrator) && isOrderInProduction" @click="sysAdminForceCompleteProduction" icon="fa-user-shield">Admin: Force Complete Production</DropdownMenuItem>
                    <DropdownMenuItem v-if="userHasUpdatePermission(PermissionTypes.AdminPermissionTypes.systemAdministrator) && canRecheckOrderProcessing" @click="sysAdminRecheckProcessing" icon="fa-user-shield">Admin: Re-check Processing</DropdownMenuItem>

                    <DropdownMenuItem v-if="canRevertDespatch" @click="orderAdminRevertDespatch" icon="fa-user-shield">Admin: Revert To Production</DropdownMenuItem>
                    <DropdownMenuItem v-if="canRecheckOrderProduction" @click="orderAdminRecheckProduction" icon="fa-user-shield">Admin: Re-check Production</DropdownMenuItem>
                    
                    <DropdownMenuDivider v-if="isQuoteReSendable || canCopyOrder" />
                    <DropdownMenuItem v-if="isQuoteReSendable" @click="sendQuote" icon="fa-envelope">Re-send Quote</DropdownMenuItem>
                    <DropdownMenuItem v-if="canCopyOrder" @click="copyOrder" icon="fa-copy">Copy Order</DropdownMenuItem>
                    <DropdownMenuDivider v-if="canPrintSheet || canPrintProductionLabels" />
                    <DropdownMenuItem @click="displayOrderReport()" icon="fa-print" v-if="canPrintSheet">Order Sheet</DropdownMenuItem>
                    <DropdownMenuItem @click="downloadProductionSheets(false, true)" icon="fa-print" v-if="canPrintSheet">Customer Sheet</DropdownMenuItem>
                    <DropdownMenuItem @click="downloadProductionSheets()" icon="fa-print" v-if="canPrintSheet">Production Sheets</DropdownMenuItem>
                    <DropdownMenuItem @click="displayOrderPanelLabels(null)" icon="fa-print" v-if="canPrintProductionLabels">Production Labels</DropdownMenuItem>
                  </ActionDropMenu>

                  <ProgressTracker>
                    <ProgressTrackerItem title="Order Created" :completed="true" :cancelled="isOrderCancelledDenied" icon="fa-list-alt" :timestamp="order.createdAt" :paused="order.isOnHold">
                      <template v-slot:completed>
                        <div class="row">
                          <div class="col-lg-2">
                            <h5 class="mb-0">CREATED BY</h5>
                            {{order.requestUser.name}}
                            <template v-if="order.accountLocation">
                              <h5 class="mt-2 mb-0">LOCATION</h5>
                              {{order.accountLocation.name}}
                            </template>
                          </div>
                          <div class="col-lg-2">
                            <h5 class="mb-0">CUSTOMER</h5>
                            {{order.contact.name}}
                            <template v-if="order.contactPerson">
                              <h5 class="mt-2 mb-0">CONTACT PERSON</h5>
                              <strong>{{order.contactPerson.firstName}} {{order.contactPerson.lastName}}</strong>
                              <div v-if="order.contactPerson.email">{{order.contactPerson.email}}</div>
                              <div v-if="order.contactPerson.mobile">{{order.contactPerson.mobile}}</div>
                              <div v-if="order.contactPerson.phone">{{order.contactPerson.phone}}</div>
                            </template>
                            <template v-if="!order.contactPerson && order.customContactName">
                              <h5 class="mt-2 mb-0">CUSTOM CONTACT PERSON</h5>
                              <strong>{{order.customContactName}}</strong>
                              <div v-if="order.customContactEmail">{{order.customContactEmail}}</div>
                              <div v-if="order.customContactMobile">{{order.customContactMobile}}</div>
                            </template>
                            <template v-if="order.customerRef">
                              <h5 class="mt-2 mb-0">REFERENCE</h5>
                              {{order.customerRef}}
                            </template>
                          </div>
                          <div class="col-lg-2">
                            <template v-if="order.despatchTaskType">
                              <h5 class="mb-0">DESPATCH TYPE</h5>
                              <div class="mb-2">{{order.despatchTaskType.name}}</div>
                            </template>
                            <template v-if="order.despatchChargeType != DespatchChargeTypes.order">
                              <h5 class="mb-0">DESPATCH CHARGE TYPE</h5>
                              <div class="mb-2">{{formatLookupName(order.despatchChargeType, DespatchChargeTypeLookup)}}</div>
                            </template>

                            <template v-if="isDespatchDelivery">
                              <h5 class="mb-0">DELIVERING TO</h5>
                              <AddressDisplay
                                :address1="order.deliveryAddress1"
                                :address2="order.deliveryAddress2"
                                :suburb="order.deliverySuburb"
                                :postcode="order.deliveryPostcode"
                                :state="order.deliveryState"
                                :country="order.deliveryCountry"
                                />
                            </template>
                          </div>
                          <div class="col-lg-6">
                            <OrderCommLogList ref="recentComLog" :orderId="order.id" />
                          </div>
                        </div>
                      </template>
                    </ProgressTrackerItem>

                    <ProgressTrackerItem title="Draft" :completed="false" :current="order.status == OrderStatusTypes.draft" :cancelled="isOrderCancelledDenied" icon="fa-pencil-ruler" v-if="order.status == OrderStatusTypes.draft" :paused="order.isOnHold">
                      <template v-slot:current>
                        <p>The order is currently in draft awaiting submission</p>

                        <ProgressTrackerButton @click="submitDraftOrder" icon="fa-check" v-if="userHasUpdatePermission(PermissionTypes.OrderPermissionTypes.order)">Approve Draft</ProgressTrackerButton>
                      </template>
                    </ProgressTrackerItem>

                    <ProgressTrackerItem
                      v-if="order.status == OrderStatusTypes.processing"
                      :current="order.status == OrderStatusTypes.processing"
                      title="Order Processing"
                      icon="fa-sync"
                      :timestamp="order.createdAt"
                      :paused="order.isOnHold">

                      <template v-slot:current>
                        <p>The order is currently being processed by the system and will be available shortly</p>
                      </template>
                    </ProgressTrackerItem>

                    <ProgressTrackerItem
                      v-if="!(isOrderCancelledDenied && !isApproved) && requiresAccountsApproval"
                      :title="isAccountsPricingApproved ? 'Order Accounts Approved' : 'Waiting Accounts Approval'"
                      :current="order.status == OrderStatusTypes.pendingAccountApproval"
                      :completed="isAccountsPricingApproved"
                      :cancelled="isOrderCancelledDenied"
                      icon="fa-thumbs-up"
                      :note="`Once accounts approved, the order will move to the next stage`"
                      :timestamp="order.dateProductionStarted"
                      :paused="order.isOnHold">
                      <template v-slot:current>
                        <p>The order is waiting for accounts approval.</p>
                        <p>Once accounts have been approved, the order will move to the next stage</p>
                        <br />
                        <OrderApprovalBar
                          v-if="order.requiresPricingApproval"
                          title="Pricing"
                          :approvalType="OrderApprovalTypes.pricing"
                          :order="order"
                          :permissionType="PermissionTypes.OrderPermissionTypes.orderPricingApproval"
                          @displayDetails="displayDetailsTab"
                          @approve="approveOrder(OrderApprovalTypes.pricing, 'Pricing')"
                          @deny="denyOrder(OrderApprovalTypes.pricing, 'Pricing')"
                          class="mt-2"
                        />
                        <OrderApprovalBar
                          v-if="order.requiresAccountsApproval"
                          title="Accounts Approval"
                          :approvalType="OrderApprovalTypes.account"
                          :order="order"
                          :permissionType="PermissionTypes.OrderPermissionTypes.orderAccountsApproval"
                          @approve="approveOrder(OrderApprovalTypes.account, 'Account')"
                          @deny="denyOrder(OrderApprovalTypes.account, 'Account')"
                          class="mt-2"
                        />
                      </template>
                      <template v-slot:completed>
                        <OrderApprovalBar
                          v-if="order.requiresPricingApproval"
                          title="Pricing"
                          :approvalType="OrderApprovalTypes.pricing"
                          :order="order"
                          :permissionType="PermissionTypes.OrderPermissionTypes.orderPricingApproval"
                          @displayDetails="displayDetailsTab"
                          class="mt-2"
                        />
                        <OrderApprovalBar
                          v-if="order.requiresAccountsApproval"
                          title="Accounts Approval"
                          :approvalType="OrderApprovalTypes.account"
                          :order="order"
                          :permissionType="PermissionTypes.OrderPermissionTypes.orderAccountsApproval"
                          class="mt-2"
                        />
                      </template>
                    </ProgressTrackerItem>

                    <ProgressTrackerItem
                      v-if="!(isOrderCancelledDenied && !isApproved) && requiresProductionApproval"
                      :title="isProductionApproved ? 'Order Production Approved' : 'Waiting Production Approval'"
                      :current="order.status == OrderStatusTypes.pendingProductionApproval"
                      :completed="isProductionApproved"
                      :cancelled="isOrderCancelledDenied"
                      icon="fa-thumbs-up"
                      :note="`Once production approved, the order will move to the next stage`"
                      :timestamp="order.dateProductionStarted"
                      :paused="order.isOnHold">
                      <template v-slot:current>
                        <p>The order is waiting for production approval.</p>
                        <p>Once production has been approved, the order will move to the next stage</p>
                        <br />
                        <OrderApprovalBar
                          v-if="order.requiresProductionApproval"
                          title="Production"
                          :approvalType="OrderApprovalTypes.production"
                          :order="order"
                          :permissionType="PermissionTypes.OrderPermissionTypes.orderProductionApproval"
                          @displayDetails="displayDetailsTab"
                          @approve="approveOrder(OrderApprovalTypes.production, 'Production')"
                          @deny="denyOrder(OrderApprovalTypes.production, 'Production')"
                        />
                      </template>
                      <template v-slot:completed>
                        <OrderApprovalBar
                          v-if="order.requiresProductionApproval"
                          title="Production"
                          :approvalType="OrderApprovalTypes.production"
                          :order="order"
                          :permissionType="PermissionTypes.OrderPermissionTypes.orderProductionApproval"
                          @displayDetails="displayDetailsTab"
                        />
                      </template>
                    </ProgressTrackerItem>

                    <ProgressTrackerItem
                      v-if="deniedApproval"
                      title="Approval Denied"
                      :current="false"
                      :completed="false"
                      :cancelled="true"
                      icon="fa-thumbs-up"
                      :timestamp="order.dateDenied"
                      :paused="order.isOnHold">
                      <template v-slot:cancelled>
                        <OrderApprovalBar
                          v-if="order.requiresProductionApproval"
                          title="Production"
                          :approvalType="OrderApprovalTypes.production"
                          :order="order"
                          :permissionType="PermissionTypes.OrderPermissionTypes.orderProductionApproval"
                          @displayDetails="displayDetailsTab"
                        />
                        <OrderApprovalBar
                          v-if="order.requiresPricingApproval"
                          title="Pricing"
                          :approvalType="OrderApprovalTypes.pricing"
                          :order="order"
                          :permissionType="PermissionTypes.OrderPermissionTypes.orderPricingApproval"
                          @displayDetails="displayDetailsTab"
                          class="mt-2"
                        />
                        <OrderApprovalBar
                          v-if="order.requiresCustomerApproval"
                          title="Customer Approval"
                          :approvalType="OrderApprovalTypes.customer"
                          :order="order"
                          :permissionType="PermissionTypes.OrderPermissionTypes.order"
                          @displayDetails="displayDetailsTab"
                          class="mt-2"
                        />
                        <OrderApprovalBar
                          v-if="order.requiresAccountsApproval"
                          title="Accounts Approval"
                          :approvalType="OrderApprovalTypes.account"
                          :order="order"
                          :permissionType="PermissionTypes.OrderPermissionTypes.orderAccountsApproval"
                          class="mt-2"
                        />
                      </template>
                    </ProgressTrackerItem>

                    <ProgressTrackerItem
                      v-if="isQuote || order.convertedFromQuote"
                      :title="order.status == OrderStatusTypes.quotePendingSent ? 'Send Quote' : 'Quote'"
                      :completed="isStatusCompleted(OrderStatusTypes.pendingCustomerApproval)"
                      :current="order.status == OrderStatusTypes.quotePendingSent"
                      :cancelled="isOrderCancelledDenied"
                      :timestamp="order.dateQuoteSent"
                      :note="`Waiting for steps above before able to send quote`"
                      icon="fa-paper-plane"
                      :paused="order.isOnHold">

                      <template v-slot:current>
                        <p>The quote is waiting to be sent to the customer for approval</p>

                        <ProgressTrackerButton @click="sendQuote" icon="fa-paper-plane">Send Quote</ProgressTrackerButton>
                        &nbsp;&nbsp;
                        <ProgressTrackerButton @click="quoteApproved" icon="fa-check" v-if="userHasUpdatePermission(PermissionTypes.OrderPermissionTypes.order)">Customer Approved Quote</ProgressTrackerButton>
                        &nbsp;&nbsp;
                        <ProgressTrackerButton @click="cancelQuote" icon="fa-times" v-if="userHasUpdatePermission(PermissionTypes.OrderPermissionTypes.order)">Cancel Quote</ProgressTrackerButton>
                      </template>
                      <template v-slot:completed>
                        <p>The quote was sent to the customer</p>
                      </template>
                    </ProgressTrackerItem>

                    <ProgressTrackerItem
                      v-if="isQuote || order.convertedFromQuote"
                      :title="order.status == OrderStatusTypes.pendingCustomerApproval ? 'Awaiting Quote Approval' : 'Quote'"
                      :current="order.status == OrderStatusTypes.pendingCustomerApproval"
                      :completed="isQuoteAccepted"
                      :cancelled="isOrderCancelledDenied"
                      icon="fa-envelope-open-dollar"
                      :timestamp="order.dateOrderCreated"
                      note="Waiting for approvals and quote to be sent to the customer"
                      :paused="order.isOnHold">
                      <template v-slot:current>
                        <p></p>

                        <ProgressTrackerButton @click="sendQuote" icon="fa-paper-plane">Resend Quote</ProgressTrackerButton>
                        &nbsp;&nbsp;
                        <ProgressTrackerButton @click="quoteApproved" icon="fa-check" v-if="userHasUpdatePermission(PermissionTypes.OrderPermissionTypes.order)">Customer Approved Quote</ProgressTrackerButton>
                        &nbsp;&nbsp;
                        <ProgressTrackerButton @click="cancelQuote" icon="fa-times" v-if="userHasUpdatePermission(PermissionTypes.OrderPermissionTypes.order)">Cancel Quote</ProgressTrackerButton>

                      </template>
                      <template v-slot:completed>
                        <p>The quote was approved by the customer and converted to an order</p>
                      </template>
                    </ProgressTrackerItem>

                    <ProgressTrackerItem
                      v-if="!(isOrderCancelledDenied && !isApproved) && requiresCustomerApproval"
                      :title="isCustomerApproved ? 'Order Customer Approved' : 'Waiting Customer Approval'"
                      :current="order.status == OrderStatusTypes.pendingCustomerApproval"
                      :completed="isStatusCompleted(OrderStatusTypes.pendingPrePaid)"
                      :cancelled="isOrderCancelledDenied"
                      icon="fa-thumbs-up"
                      :note="`Once customer has approved, the order will move to the next stage`"
                      :timestamp="order.dateProductionStarted"
                      :paused="order.isOnHold">
                      <template v-slot:current>
                        <p>The order is waiting for customer approval.</p>
                        <p>Once customer has approved, the order will move to the next stage</p>
                        <br />
                        <OrderApprovalBar
                          v-if="order.requiresCustomerApproval"
                          title="Production"
                          :approvalType="OrderApprovalTypes.customer"
                          :order="order"
                          :permissionType="PermissionTypes.OrderPermissionTypes.order"
                          :emailEnabled="true"
                          @displayDetails="displayDetailsTab"
                          @email="sendOrder"
                          @approve="approveOrder(OrderApprovalTypes.customer, 'Customer')"
                          @deny="denyOrder(OrderApprovalTypes.customer, 'Customer')"
                        />
                      </template>
                      <template v-slot:completed>
                        <OrderApprovalBar
                          v-if="order.requiresCustomerApproval"
                          title="Customer"
                          :approvalType="OrderApprovalTypes.customer"
                          :order="order"
                          :permissionType="PermissionTypes.OrderPermissionTypes.order"
                          @displayDetails="displayDetailsTab"
                        />
                      </template>
                    </ProgressTrackerItem>

                    <ProgressTrackerItem
                      v-if="isOrderPaymentTypePrePaid"
                      title="Pre-Paid"
                      :current="order.status == OrderStatusTypes.pendingPrePaid"
                      :completed="isStatusCompleted(OrderStatusTypes.pendingExternalSetup)"
                      :cancelled="isOrderCancelledDenied"
                      :timestamp="order.dateProductionStarted || order.dateDespatchStarted"
                      icon="fa-file-invoice-dollar"
                      :paused="order.isOnHold">

                      <template v-slot:current>
                        <div class="mt-4"> </div>
                        <ProgressTrackerButton @click="sendOrder" icon="fa-paper-plane">Send Order</ProgressTrackerButton>
                        &nbsp;&nbsp;
                        <ProgressTrackerButton v-if="userHasCreatePermission(PermissionTypes.AccountsPermissionTypes.orderEntryPayment)" @click="addPayment" icon="fa-credit-card">Add Payment</ProgressTrackerButton>
                        &nbsp;&nbsp;
                        <ProgressTrackerButton v-if="userHasUpdatePermission(PermissionTypes.OrderPermissionTypes.orderAccountsApproval)" @click="skipPrePaid" icon="fa-forward">Override</ProgressTrackerButton>

                        <div class="mt-4"> </div>
                        <OrderPrePaymentDetails :order="order" />
                      </template>
                      <template v-slot:waiting>
                        <div class="mt-4"> </div>
                        <OrderPrePaymentDetails :order="order" />
                      </template>
                      <template v-slot:all>
                        <div class="row mb-2">
                          <div class="col-6">
                            <ActivityLogEventList ref="prePaidActivityLogEventList" title="Pre-paid Activity Log" :linkType="LinkTypes.order" :linkId="order.id" :eventTag="OrderEventTagTypes.orderPrepaidSkipped" :activityStreamURL="activityStreamURL" />
                          </div>
                        </div>
                          
                        <OrderPaymentList ref="orderPrePaidPaymentList" :orderId="order.id" />
                      </template>
                    </ProgressTrackerItem>

                    <!--
                      General payment for accounts based orders
                      Shown here as account orders can have payments received against them
                     -->
                    <ProgressTrackerItem
                      v-if="!isOrderPaymentTypePrePaid && !isOrderPaymentTypeCOD && orderHasPayments"
                      title="Payments"
                      :current="false"
                      :completed="isStatusCompleted(OrderStatusTypes.pendingExternalSetup)"
                      :cancelled="isOrderCancelledDenied"
                      icon="fa-file-invoice-dollar"
                      :paused="order.isOnHold">

                      <template v-slot:all>
                        <OrderPaymentList ref="orderAccountPaymentList" :orderId="order.id" />
                      </template>
                    </ProgressTrackerItem>

                    <ProgressTrackerItem
                      v-if="isOrderRequiresExternalSetup"
                      title="External Setup"
                      :current="order.status == OrderStatusTypes.pendingExternalSetup"
                      :completed="isStatusCompleted(OrderStatusTypes.preProduction)"
                      :cancelled="isOrderCancelledDenied"
                      :timestamp="order.dateProductionStarted || order.dateDespatchStarted"
                      icon="fa-external-link-alt"
                      note="Waiting for the order to progress"
                      :paused="order.isOnHold">

                      <template v-slot:all>
                        <div v-if="order.status == OrderStatusTypes.pendingExternalSetup">
                          <p>The order is waiting for setup of purchase orders for the external items</p>
                          <p>Once setup, the order will move to the next stage</p>
                          <br />
                        </div>
                        <OrderExternalSetupBar
                          :order="order"
                          :available="externalApprovalsAvailable"
                          :isExternalSetupComplete="isStatusCompleted(OrderStatusTypes.preProduction)"
                          @displayDetails="displayDetailsTab"
                          @setup="startExternalItemsSetup()"
                          @emailPurchaseOrder="emailExternalPurchaseOrder($event)">
                        </OrderExternalSetupBar>
                      </template>
                    </ProgressTrackerItem>

                    <ProgressTrackerItem
                      v-if="!(isOrderCancelledDenied && !isApproved) && (isPreProductionEnabled || order.status == OrderStatusTypes.preProduction)"
                      title="Pre Production"
                      :current="order.status == OrderStatusTypes.preProduction"
                      :completed="isStatusCompleted(OrderStatusTypes.production)"
                      :cancelled="isOrderCancelledDenied"
                      icon="fa-user-clock"
                      :timestamp="order.datePendingProductionStarted"
                      note="Waiting for steps above before starting pre-production"
                      :paused="order.isOnHold">
                      <template v-slot:current>
                        <p>The order is currently in pre-production</p>
                        <p>Once the following items are completed, the order will move to the next stage</p>
                        <br />

                        <OrderChecklistItemList
                          :order="order"
                          @sendOrder="sendOrder"
                          @printProductionSheets="downloadProductionSheets(true)"
                          @printCustomerSheets="downloadProductionSheets(true, true)"
                          @printLabels="displayOrderPanelLabels(null, true)"
                          @skip="skipChecklistItem" />

                      </template>
                      <template v-slot:completed>
                        <p>Pre-production has been completed</p>
                        <br />

                        <OrderChecklistItemList :order="order" />
                      </template>
                    </ProgressTrackerItem>

                    <ProgressTrackerItem
                      v-if="!(isOrderCancelledDenied && !isApproved) && order.requiresProduction"
                      title="Production"
                      :current="order.status == OrderStatusTypes.production"
                      :completed="isStatusCompleted(OrderStatusTypes.pendingCODPaid)"
                      :cancelled="isOrderCancelledDenied"
                      icon="fa-tools"
                      :timestamp="order.dateProductionCompleted"
                      note="Waiting for steps above before starting production"
                      :paused="order.isOnHold">
                      <template v-slot:current>
                        <p>The order items are currently in production</p>
                        <OrderProductionPreview :order="order" :compactMode="true" />

                        <br /><br /><br /><br />
                        <ProgressTrackerButton @click="displayProductionTab" icon="fa-tools">View Production</ProgressTrackerButton>
                      </template>
                      <template v-slot:completed>
                        <p>Production has been completed</p>
                        <OrderProductionPreview :order="order" :compactMode="true" />
                      </template>
                    </ProgressTrackerItem>

                    <ProgressTrackerItem
                      v-if="isOrderPaymentTypeCOD"
                      title="Cash On Delivery"
                      :current="order.status == OrderStatusTypes.pendingCODPaid"
                      :completed="isStatusCompleted(OrderStatusTypes.despatch)"
                      :cancelled="isOrderCancelledDenied"
                      icon="fa-file-invoice-dollar">
                      <template v-slot:current>
                        <div class="mt-4"> </div>
                        <ProgressTrackerButton @click="sendOrder" icon="fa-paper-plane">Send Order</ProgressTrackerButton>
                        &nbsp;&nbsp;
                        <ProgressTrackerButton v-if="userHasCreatePermission(PermissionTypes.AccountsPermissionTypes.orderEntryPayment)" @click="addPayment" icon="fa-credit-card">Add Payment</ProgressTrackerButton>
                        &nbsp;&nbsp;
                        <ProgressTrackerButton v-if="userHasUpdatePermission(PermissionTypes.OrderPermissionTypes.orderAccountsApproval)" @click="skipCashOnDelivery" icon="fa-forward">Override</ProgressTrackerButton>
                      </template>
                      <template v-slot:all>
                        <ActivityLogEventList ref="codActivityLogEventList" title="Cash On Delivery Activity Log" :linkType="LinkTypes.order" :linkId="order.id" :eventTag="OrderEventTagTypes.orderCODSkipped" :activityStreamURL="activityStreamURL" />

                        <OrderPaymentList ref="orderCODPaymentList" :orderId="order.id" />
                      </template>
                    </ProgressTrackerItem>

                    <ProgressTrackerItem
                      v-if="!(isOrderCancelledDenied && !isApproved)"
                      :title="despatchProgressTitle"
                      :current="order.status == OrderStatusTypes.despatch"
                      :completed="order.status == OrderStatusTypes.completed || order.status == OrderStatusTypes.ncrPendingReturn"
                      :cancelled="isOrderCancelledDenied"
                      :icon="getTaskTypeIcon(order.despatchTaskType)"
                      :timestamp="order.dateDespatchCompleted"
                      note="Waiting for production to be completed"
                      :paused="order.isOnHold">
                      <template v-slot:all>
                        <OrderProductionPreview :order="order" :compactMode="true" v-if="!order.requiresProduction" />

                        <OrderDespatchTaskList ref="orderDespatchList" :order="order" :canCreateTask="canAddDespatchTask" @taskCreated="reloadData()" />
                      </template>
                    </ProgressTrackerItem>

                    <ProgressTrackerItem
                      v-if="!(isOrderCancelledDenied && !isApproved) && isNCR"
                      title="NCR Returns"
                      :current="order.status == OrderStatusTypes.ncrPendingReturn"
                      :completed="order.status == OrderStatusTypes.completed"
                      :cancelled="isOrderCancelledDenied"
                      icon="fa-repeat"
                      :timestamp="order.dateDespatchCompleted"
                      note="Waiting for despatch to be completed"
                      :paused="order.isOnHold">
                      <template v-slot:current>
                        <p>Waiting for original order items to be returned and inspected</p>
                        <br />

                        <OrderApprovalBar
                          v-for="orderItem of ncrItemList"
                          :key="orderItem.id"
                          :title="`${orderItem.stock.code} - ${orderItem.glassHeight}x${orderItem.glassWidth}`"
                          approveButtonTitle="Approve"
                          denyButtonTitle="Deny"
                          :approvalType="OrderApprovalTypes.ncrOrderItem"
                          :order="order"
                          :orderItem="orderItem"
                          :permissionType="PermissionTypes.OrderPermissionTypes.orderProductionApproval"
                          @approve="acceptNCRItem(orderItem)"
                          @deny="rejectNCRItem(orderItem)"
                          @displayDetails="displayNCRPopover($event, orderItem)"
                        />

                        <div class="row" v-if="order.ncrAdditionalDeliveryCharge > 0">
                          <div class="col-12">
                            <h5>Additional Fees:</h5>
                            <p class="status-denied">NCR Delivery Fee: {{order.ncrAdditionalDeliveryCharge | formatCurrency}}</p>
                          </div>
                        </div>
                        
                      </template>
                      <template v-slot:completed>
                        <OrderApprovalBar
                          v-for="orderItem of ncrItemList"
                          :key="orderItem.id"
                          :title="`${orderItem.stock.code} - ${orderItem.glassHeight}x${orderItem.glassWidth}`"
                          :approvalType="OrderApprovalTypes.ncrOrderItem"
                          :order="order"
                          :orderItem="orderItem"
                          :permissionType="PermissionTypes.OrderPermissionTypes.orderProductionApproval"
                          @displayDetails="displayNCRPopover($event, orderItem)"
                        />

                        <div class="row" v-if="order.ncrAdditionalDeliveryCharge > 0">
                          <div class="col-12">
                            <h5>Additional Fees:</h5>
                            <p class="status-denied">NCR Delivery Fee: {{order.ncrAdditionalDeliveryCharge | formatCurrency}}</p>
                          </div>
                        </div>
                        <div class="row">
                          <div class="col-12">
                            <ProgressTrackerButton @click="displayNCRAccounts" icon="fa-repeat" v-if="userHasReadPermission(PermissionTypes.AccountsPermissionTypes.invoice) || userHasReadPermission(PermissionTypes.AccountsPermissionTypes.creditNote)">View NCR Accounts</ProgressTrackerButton>
                          </div>
                        </div>
                      </template>
                    </ProgressTrackerItem>

                    <OrderItemNCRPopover ref="ncrPopover" />

                    <ProgressTrackerItem
                      v-if="deniedApproval"
                      title="Order Denied"
                      :cancelled="true"
                      :timestamp="order.dateDenied"
                      :paused="order.isOnHold">
                      <template v-slot:cancelled>
                        <p>The order may be revised and processed through approval again</p>

                        <ProgressTrackerButton @click="restoreOrder" icon="fa-undo" v-if="userHasUpdatePermission(PermissionTypes.OrderPermissionTypes.order)">Restore Order</ProgressTrackerButton>
                      </template>
                    </ProgressTrackerItem>

                    <ProgressTrackerItem
                      v-if="!deniedApproval"
                      title="Invoiced"
                      :completed="order.status == OrderStatusTypes.completed"
                      :cancelled="isOrderCancelledDenied"
                      icon="fa-file-invoice-dollar">
                      <template v-slot:all>
                        <OrderInvoiceList ref="orderInvoiceList" :orderStatus="order.status" :orderId="order.id" />
                      </template>
                    </ProgressTrackerItem>

                    <ProgressTrackerItem
                      v-if="!isOrderCancelledDenied"
                      title="Completed"
                      :completed="order.status == OrderStatusTypes.completed"
                      :cancelled="isOrderCancelledDenied"
                      icon="fa-check"
                      :timestamp="order.dateClosed"
                      note="Waiting for customer to receive goods"
                      :paused="order.isOnHold">
                      <template v-slot:completed>
                        <p>Completed</p>
                      </template>
                    </ProgressTrackerItem>

                    <ProgressTrackerItem
                      v-if="isOrderCancelled"
                      title="Order Cancelled"
                      :cancelled="true"
                      :timestamp="order.dateClosed"
                      :paused="order.isOnHold">
                      <template v-slot:cancelled>
                        <div class="row">
                          <div class="col-lg-12">
                            <h5>CANCELLED REASON</h5>
                            {{order.cancelledReason}}
                          </div>
                        </div>
                      </template>
                    </ProgressTrackerItem>


                  </ProgressTracker>

                </div>
              </div>
              
            </TabPage>

            <TabPage tabid="tab-order-details">
              <div class="row" v-if="order.isOnHold">
                <div class="col-12">
                  <OrderHoldBar
                    :order="order"
                    :canUnHoldOrder="canUnHoldOrder"
                    :canCancelOrder="canCancelOrder"
                    @unHoldOrder="unHoldOrder"
                    @cancelOrder="cancelOrder" />
                </div>
              </div>
              <DataPageArea title="Details">
                <template slot="actionArea">
                  <ActionDropMenu v-if="(isOrderInfoEditable || isOrderItemsEditable || isOptionOrderEditorAvailable) && !itemList.editing" class="float-right">
                    <DropdownMenuItem @click="startOrderEditorViewer" icon="fa-shapes" v-if="isOptionOrderEditorAvailable">View Order In Editor</DropdownMenuItem>
                    <DropdownMenuDivider v-if="(isOrderInfoEditable || isOrderItemsEditable) && isOptionOrderEditorAvailable" />

                    <DropdownMenuItem @click="editOrder" icon="fa-pencil" v-if="isOrderInfoEditable">Edit Order</DropdownMenuItem>
                    <DropdownMenuItem @click="editOrderDespatch" icon="fa-truck" v-if="isOrderInfoEditable">Edit Order Despatch</DropdownMenuItem>
                    <DropdownMenuItem @click="editOrderItemList" icon="fa-list" v-if="isOrderItemsEditable">Edit Order Items</DropdownMenuItem>
                  </ActionDropMenu>
                </template>
                <template slot="dataArea">
                  
                  <DataItem title="Customer" v-if="order.contact">{{order.contact.name}}</DataItem>

                  <DataItem title="Contact Person" v-if="order.contactPerson">
                    {{order.contactPerson.firstName}} {{order.contactPerson.lastName}}
                    <div v-if="order.contactPerson.email">{{order.contactPerson.email}}</div>
                    <div v-if="order.contactPerson.mobile">{{order.contactPerson.mobile}}</div>
                    <div v-if="order.contactPerson.phone">{{order.contactPerson.phone}}</div>
                  </DataItem>

                  <DataItem title="Custom Contact Person" v-if="!order.contactPerson && order.customContactName">
                    {{order.customContactName}} <span v-if="order.customContactEmail">-</span> {{order.customContactEmail}}
                    <div v-if="order.customContactMobile">{{order.customContactMobile}}</div>
                    <div v-if="order.customContactDetails">
                      <span v-html="convertNewlinesToBreaks(order.customContactDetails)" />
                    </div>
                  </DataItem>

                  <DataItem title="Despatch Info">
                    <div v-if="order.despatchTaskType">{{order.despatchTaskType.name}}</div>
                    <div>Despatch Charge - {{formatLookupName(order.despatchChargeType, DespatchChargeTypeLookup)}}</div>
                  </DataItem>
                  
                  <DataItem title="Delivery Address" v-if="isDespatchDelivery">
                    <LabelTags :labelList="addressDeliveryLabelList" />
                    <AddressDisplay
                      :address1="order.deliveryAddress1"
                      :address2="order.deliveryAddress2"
                      :suburb="order.deliverySuburb"
                      :postcode="order.deliveryPostcode"
                      :state="order.deliveryState"
                      :country="order.deliveryCountry"
                      />
                  </DataItem>
                  <DataItem title="Completion Date" v-if="!order.dateRequested">
                    <span>Estimated: {{order.dateCurrentlyDue | formatDate}}</span>
                    <span v-if="isStatusDraft"> <Tooltip label="Order is in draft and the estimated date may change when draft is approved"> <i class="far fa-exclamation-circle status-order-notice"></i></Tooltip></span>
                  </DataItem>
                  <DataItem title="Completion Date" v-if="order.dateRequested">
                    <span class="status-order-notice">Requested: {{order.dateRequested | formatDateTime}}</span>
                  </DataItem>

                  <DataItem title="Notes / Instructions"><span v-if="order.notes" v-html="convertNewlinesToBreaks(order.notes)" /></DataItem>
                  <DataItem title="Delivery Instructions" v-if="order.deliveryInstructions">{{order.deliveryInstructions}}</DataItem>
                  <DataItem title="Customer Reference">{{order.customerRef}}</DataItem>
                  <DataItem title="Urgent Reason" v-if="order.isUrgent"><span v-if="order.urgentReason" v-html="convertNewlinesToBreaks(order.urgentReason)" /></DataItem>
                  
                </template>
              </DataPageArea>

              <div class="row mt-3">
                <div class="col-lg-12">
                  <h3>Items</h3>

                  <FormGroup :form="itemList.form">
                    <template v-slot:errorMessage>
                      <div v-if="itemList.forceProductionUpdateAvailable" class="mt-2">
                        <NotificationBarButton @click="forceProductionItemListUpdate" icon="fa-user-shield">Admin: Force Update</NotificationBarButton>
                      </div>
                    </template>

                    <div v-if="itemList.loading && !itemList.loaded" class="text-center">
                      Loading Order Items...<br />
                      <Spinner />
                    </div>
                    <OrderItemList
                      v-if="itemList.loaded"
                      ref="orderItemList"
                      :value="itemList.itemData"
                      :editing="itemList.editing"
                      :isDespatchDelivery="isDespatchDelivery"
                      :contactId="order.contactId"
                      :orderType="order.type"
                      :showTotals="!isNCR"

                      :optionNewItemGlassAvailable="canAddGlassOrderItems"
                      :optionNewItemGeneralAvailable="canAddGeneralOrderItems"
                      :optionNewItemTemplateAvailable="canAddGlassOrderItems"
                      :optionNewItemAssemblyAvailable="canAddGlassOrderItems"
                      :optionCancelItemAvailable="isOrderItemsCancellable && userHasUpdatePermission(PermissionTypes.OrderPermissionTypes.orderItemCancel)"
                      :optionOrderEditorAvailable="isOptionOrderEditorAvailable"
                      :optionCustomerApprovalAvailable="isOptionCustomerApprovalAvailable"

                      @itemCancel="itemCancel"
                      @startOrderEditor="startOrderEditor"
                      @loadingStatusUpdate="orderItemListLoading = $event"
                    />

                    <FormSubmit v-if="itemList.editing" label="Update" :submitting="itemList.submitting" :disabled="orderItemListLoading" @submitted="submitItemList(false)" @cancelled="cancelEditingItemList" />
                  </FormGroup>
                </div>
              </div>
            </TabPage>


            <TabPage tabid="tab-order-production" v-if="order.requiresProduction">
              <div class="row" v-if="order.isOnHold">
                <div class="col-12">
                  <OrderHoldBar
                    :order="order"
                    :canUnHoldOrder="canUnHoldOrder"
                    :canCancelOrder="canCancelOrder"
                    @unHoldOrder="unHoldOrder"
                    @cancelOrder="cancelOrder" />
                </div>
              </div>

              <DataPageArea title="Production">
                <template slot="dataArea">                 
                  <DataItem title="Despatch Type" v-if="order.despatchTaskType">{{order.despatchTaskType.name}}</DataItem>
                  <DataItem title="Estimated Completion Time">
                    <span v-if="order.dateCurrentlyDue">{{order.dateCurrentlyDue | formatDateTime}}</span>
                  </DataItem>
                  <DataItem title="Production Started Time">
                    <span v-if="order.dateProductionStarted">{{order.dateProductionStarted | formatDateTime}}</span>
                  </DataItem>
                  <DataItem title="Production Completed Time">
                    <span v-if="order.dateProductionCompleted">{{order.dateProductionCompleted | formatDateTime}}</span>
                  </DataItem>
                  <DataItem title="Despatch Started Time">
                    <span v-if="order.dateDespatchStarted">{{order.dateDespatchStarted | formatDateTime}}</span>
                  </DataItem>
                  <DataItem title="Despatch Completed Time">
                    <span v-if="order.dateDespatchCompleted">{{order.dateDespatchCompleted | formatDateTime}}</span>
                  </DataItem>
                </template>
              </DataPageArea>

              <FormHorizontalRule class="my-3" />

              <div class="row mt-3">
                <div class="col-lg-12">
                  <ContentTitle>Production Details</ContentTitle>
                </div>
              </div>
              <div class="row">
                <div class="col-12 p-3">
                  <OrderProductionPreview :order="order" :compactMode="false" :showStickyHeader="true" />
                </div>
              </div>
            </TabPage>


            <TabPage tabid="tab-order-documents">
              <div class="row">
                <div class="col-lg-12">
                  <DocumentList
                    title="Documents"
                    :linkType="LinkTypes.order"
                    :linkId="order.id"
                    :permissionResource="PermissionTypes.OrderPermissionTypes.order"
                    class="mt-2" />
                </div>
              </div>
            </TabPage>

            <TabPage tabid="tab-order-ncrlist" v-if="hasLinkedNCROrders">
              <div class="row">
                <div class="col-lg-12">
                  <ContentTitle>Linked NCR Orders</ContentTitle>

                  <StaticDataTable class="static-table-list" :striped="false">
                    <template v-slot:header>
                      <tr>
                        <th>Order</th>
                        <th>Reference No.</th>
                        <th>Status</th>
                        <th>Created</th>
                        <th>Estimated Completion</th>
                      </tr>
                    </template>
                    <template v-slot:body>
                      <tr v-for="order of ncrOrderMapList" :key="order.id">
                        <td>
                          <span v-html="order.indentDisplay" />
                          <strong><LinkOrder :name="order.sequenceNumber ? '#'+order.sequenceNumber : 'Pending'" :id="order.id" /></strong>
                        </td>
                        <td>{{formatHyphenEmptyValue(order.customerRef)}}</td>
                        <td><LabelBadge :labelClass="`label-order-status-${order.status}`">{{order.status | formatLookupName(OrderStatusTypeLookup)}}</LabelBadge></td>
                        <td>{{order.dateOrderCreated | formatDateTime}}</td>
                        <td>{{order.dateCurrentlyDue | formatDateTime}}</td>
                      </tr>
                    </template>
                  </StaticDataTable>

                  
                </div>
              </div>
            </TabPage>
            
            <TabPage tabid="tab-order-activitylog">
              <div class="row">
                <div class="col-lg-12">
                  <ActivityLogStream ref="activityStream" :url="activityStreamURL" :linkType="LinkTypes.order" :linkId="order.id">
                    <template v-slot:title><h3>Recent Activity</h3></template>
                  </ActivityLogStream>
                </div>
              </div>
            </TabPage>
          </template>
        </TabbedBox>
      </div>
    </div>

    <div class="order-editor-app" v-if="showOrderEditor">
      <OrderEditorApp 
        v-model="itemList.itemData"
        :editing="itemList.editing"
        :orderId="order.id"
        :contactId="order.contactId"
        :sequenceNumber="order.sequenceNumber"
        :customerData="order.contact"
        @close="finishOrderEditor"
      />
    </div>
  </div>
</template>

<script>
import _ from 'lodash'
import { mapState } from "vuex"

import form from '@/mixins/form'
import detectUnload from '@/mixins/detectUnload'
import Utils from '@/utils/utils'
import pdfMixin from '@/mixins/pdf'
import orderItemMixin from '@/modules/orders/mixins/orderItemMixin'
import orderItemDiagramMixin from '@/modules/orders/mixins/orderItemDiagramMixin'
import taskDespatchMixin from '@/modules/tasks/mixins/taskDespatchMixin'

import ProgressTracker from '@/components/layout/progressTracker.vue'
import ProgressTrackerItem from '@/components/layout/progressTrackerItem.vue'
import ProgressTrackerButton from '@/components/layout/progressTrackerButton.vue'
import ActivityLogEventList from '@/components/activityLogEventList'
import DocumentList from '@/components/documentList.vue'
import LabelOrderStatusBadge from '@/modules/orders/components/labelOrderStatusBadge'
import OrderItemList from '@/modules/orders/components/orderItemList'
import OrderApprovalBar from '@/modules/orders/components/orderApprovalBar'
import OrderExternalSetupBar from '@/modules/orders/components/orderExternalSetupBar'
import OrderProductionPreview from '@/modules/orders/components/orderProductionPreview'
import OrderItemNCRPopover from '@/modules/orders/components/orderItemNCRPopover'
import SendExternalPurchaseOrderDialog from '@/modules/orders/views/sendExternalPurchaseOrderDialog'
import OrderHoldBar from '@/modules/orders/components/orderHoldBar'
import OrderDespatchTaskList from '@/modules/orders/components/orderDespatchTaskList'
import OrderInvoiceList from '@/modules/orders/components/orderInvoiceList'
import OrderPaymentList from '@/modules/orders/components/orderPaymentList'
import OrderChecklistItemList from '@/modules/orders/components/orderChecklistItemList'
import OrderCommLogList from '@/modules/orders/components/orderCommLogList'
import OrderPrePaymentDetails from '@/modules/orders/components/orderPrePaymentDetails'

import EditOrderDialog from '@/modules/orders/views/editOrderDialog'
import EditOrderDespatchDialog from '@/modules/orders/views/editOrderDespatchDialog'
import CancelOrderItemDialog from '@/modules/orders/views/cancelOrderItemDialog'
import CancelOrderDialog from '@/modules/orders/views/cancelOrderDialog'
import SendQuoteDialog from '@/modules/orders/views/sendQuoteDialog'
import SendOrderDialog from '@/modules/orders/views/sendOrderDialog'
import OrderActionDialog from '@/modules/orders/views/orderActionDialog'
import OrderHoldDialog from '@/modules/orders/views/orderHoldDialog'
import OrderDenyNCRDialog from '@/modules/orders/views/orderDenyNCRDialog'
import OrderNCRAccountsDialog from '@/modules/orders/views/orderNCRAccountsDialog'
import AddOrderPaymentDialog from '@/modules/orders/views/addOrderPaymentDialog'
import OrderExternalItemUpdateDialog from '@/modules/orders/views/orderExternalItemUpdateDialog'

import OrderEditorApp from '@glass-designer/views/app'

import { LinkTypes, OrderEventTagTypes, ApprovalTypes, PricingRequestStatusTypes, InventoryStockTypes, InventoryStockTypeLookup } from '@/store/types'
import { OrderTypes, OrderTypeLookup, OrderStatusTypes, OrderStatusTypeLookup, DescriptorLookupTypes, UOMTypeUnitsLookup, OrderApprovalTypes, CreditTypes, OrderPaymentTypes, OrderPaymentTypeLookup } from '@/store/types'
import { OrderChecklistItemTypeLookup, OrderItemExternalUpdateTypes, PurchaseOrderStatusTypes, OrderHoldTypes, AccountSettingKeys, DespatchChargeTypes, DespatchChargeTypeLookup } from '@/store/types'


export default {
  name: "Order",
  mixins: [form, detectUnload, pdfMixin, orderItemMixin, orderItemDiagramMixin, taskDespatchMixin],
  components: {
    ProgressTracker,
    ProgressTrackerItem,
    ProgressTrackerButton,
    DocumentList,
    ActivityLogEventList,
    LabelOrderStatusBadge,
    OrderItemList,
    OrderApprovalBar,
    OrderExternalSetupBar,
    OrderProductionPreview,
    OrderItemNCRPopover,
    OrderHoldBar,
    OrderDespatchTaskList,
    OrderInvoiceList,
    OrderPaymentList,
    OrderChecklistItemList,
    OrderCommLogList,
    OrderPrePaymentDetails,
    OrderEditorApp
  },
  data() {
    return {
      sendQuoteOnLoad: false,
      orderId: null,
      order: {},
      itemList: {
        itemData: [],
        form: {},
        overrideWarnings: false,
        loaded: false,
        loading: false,
        editing: false,
        submitting: false,
        forceProductionUpdateAvailable: false
      },

      ncrOrderMapList: [],

      // Config
      orderStampConfig: {},

      // Order Editor
      edgeworkConfigList: [],
      showOrderEditor: false,

      orderItemListLoading: false
    }
  },
  created() {
    this.LinkTypes = LinkTypes
    this.OrderEventTagTypes = OrderEventTagTypes
    this.InventoryStockTypeLookup = InventoryStockTypeLookup
    this.OrderStatusTypes = OrderStatusTypes
    this.OrderTypes = OrderTypes
    this.OrderTypeLookup = OrderTypeLookup
    this.OrderStatusTypeLookup = OrderStatusTypeLookup
    this.OrderApprovalTypes = OrderApprovalTypes
    this.ApprovalTypes = ApprovalTypes
    this.OrderPaymentTypeLookup = OrderPaymentTypeLookup
    this.DespatchChargeTypes = DespatchChargeTypes
    this.DespatchChargeTypeLookup = DespatchChargeTypeLookup
  },
  beforeRouteLeave (to, from, next) {
    if(!this.itemList.editing)
      return next(true)
    
    this.confirmMessage(`Cancel Editing Items?`, `You have unsaved changes while editing items, are you sure you want to leave?`)
    .then(success => {
      next(success)
    })
    .catch(function () {
      next(false)
    })
  },
  mounted() {
    this.$data.sendQuoteOnLoad = this.$route.query.created || false
    this.$data.orderId = this.$route.params.id

    // Subscribe to all relevant endpoints
    this.subscribeEndpoint(`/orders/${this.$data.orderId}`, () => {
      // Ignore reload notifications if editing the order
      if(this.isEditingOrderItems)
        return

      this.reloadData()
    })

    // Load the order
    this.requestData()
  },
  beforeRouteUpdate (to, from, next) {
    next()

    this.unsubscribeEndpoint(`/orders/${this.$data.orderId}`)
    this.$data.orderId = this.$route.params.id

    this.subscribeEndpoint(`/orders/${this.$data.orderId}`, () => {
      // Ignore reload notifications if editing the order
      if(this.isEditingOrderItems)
        return
      
      this.requestData()
    })

    this.pageRefreshed()
    this.requestData()
  },
  beforeDestroy() {
    this.unsubscribeEndpoint(`/orders/${this.$data.orderId}`)
  },
  computed: {
    ...mapState({
      accountSettings: state => state.account.accountSettings
    }),
    isStatusDraft() {
      return this.order.status == OrderStatusTypes.draft
    },
    isStatusPendingPrePaid() {
      return this.order.status == OrderStatusTypes.pendingPrePaid
    },
    isOrderProcessing() {
      return this.order.status == OrderStatusTypes.processing
    },
    isDespatchDelivery() {
      return this.order.despatchTaskType && this.order.despatchTaskType.isLocationTrackingEnabled
    },
    isDespatchPickup() {
      return !this.isDespatchDelivery
    },
    isOrderPendingApproval() {
      return this.order.status == OrderStatusTypes.pendingAccountApproval || this.order.status == OrderStatusTypes.pendingProductionApproval
    },
    isOrderRequiresExternalSetup() {
      return this.order.requiresExternalApproval
    },
    isOrderInfoEditable() {
      // Basic Info can be modified up until completion/cancellation
      if(this.isOrderCompleted || this.isOrderCancelled)
        return false
      if(this.isOrderProcessing)
        return false
      
      return this.userHasUpdatePermission(this.PermissionTypes.OrderPermissionTypes.orderEdit)
    },
    isOrderItemsEditable() {
      var validEditItemStatus = [
        OrderStatusTypes.draft,
        OrderStatusTypes.pendingAccountApproval,
        OrderStatusTypes.pendingProductionApproval,
        OrderStatusTypes.quotePendingSent,
        OrderStatusTypes.pendingCustomerApproval,
        OrderStatusTypes.pendingPrePaid,
        OrderStatusTypes.pendingExternalSetup,
        OrderStatusTypes.preProduction,
        OrderStatusTypes.production,
        OrderStatusTypes.pendingCODPaid,
        OrderStatusTypes.despatch
      ]

      // Order items can be modified up until & during production / despatch
      // During production per-item restrictions are applied
      // During despatch is only editable for non-process orders
      if(validEditItemStatus.indexOf(this.order.status) == -1)
        return false

      // If in production, require orderProductionEditing permission
      if(this.isOrderInProduction)
        return this.userHasUpdatePermission(this.PermissionTypes.OrderPermissionTypes.orderProductionEditing)

      // Despatch / pendingCOD for non-production orders
      // Also requires orderProductionEditing permission
      if(this.order.status == OrderStatusTypes.despatch || this.order.status == OrderStatusTypes.pendingCODPaid) {
        if(this.order.requiresProduction)
          return false

        return this.userHasUpdatePermission(this.PermissionTypes.OrderPermissionTypes.orderProductionEditing)
      }

      return this.userHasUpdatePermission(this.PermissionTypes.OrderPermissionTypes.orderItemEdit)
    },
    isOrderItemsCancellable() {
      var validEditItemStatus = [
        OrderStatusTypes.draft,
        OrderStatusTypes.pendingAccountApproval,
        OrderStatusTypes.pendingProductionApproval,
        OrderStatusTypes.quotePendingSent,
        OrderStatusTypes.pendingCustomerApproval,
        OrderStatusTypes.pendingPrePaid,
        OrderStatusTypes.pendingExternalSetup,
        OrderStatusTypes.preProduction,
        OrderStatusTypes.production,
        OrderStatusTypes.pendingCODPaid,  // Needs pendingCOD/despatch status for stock ordered items that skip production
        OrderStatusTypes.despatch         // Needs despatch status for stock ordered items that skip production
      ]

      // Order items can be cancelled up until & during production
      // The server will handle attempts at cancellation of panels already completed
      // The server will handle cancellation fees for tasks / materials already processed
      if(validEditItemStatus.indexOf(this.order.status) == -1)
        return false

      return true
    },
    isOrderCompleted() {
      return this.order.status == OrderStatusTypes.completed
    },
    isOrderCancelled() {
      return this.order.status == OrderStatusTypes.cancelled
    },
    isOrderDenied() {
      return this.order.status == OrderStatusTypes.denied
    },
    isOrderInDespatch() {
      return this.order.status == OrderStatusTypes.despatch
    },
    isOrderInProduction() {
      return this.order.status == OrderStatusTypes.production
    },
    isOrderCancelledDenied() {
      return this.order.status == OrderStatusTypes.cancelled || this.order.status == OrderStatusTypes.denied
    },
    isOrderPaymentTypePrePaid() {
      return this.order.orderPaymentType == OrderPaymentTypes.prepaid
    },
    isOrderPaymentTypeCOD() {
      return this.order.orderPaymentType == OrderPaymentTypes.cod
    },
    isOrderPaymentTypeAccount() {
      return this.order.orderPaymentType == OrderPaymentTypes.account
    },
    orderHasPayments() {
      return this.order.reservedPayments && this.order.reservedPayments.length > 0
    },
    isQuote() {
      return this.order.type == OrderTypes.quote
    },
    isNCR() {
      return this.order.type == OrderTypes.nonChargeableRework
    },
    isEditingOrderItems() {
      return this.itemList.editing
    },
    isQuoteReSendable() {
      if(!this.isQuote || this.isOrderPendingApproval || this.isOrderCompleted || this.isStatusDraft || this.isOrderCancelledDenied)
        return false

      return this.userHasUpdatePermission(this.PermissionTypes.OrderPermissionTypes.order)
    },
    isQuoteAccepted() {
      // If converted to an order, consider the quote accepted
      return this.order.type == OrderTypes.order && this.order.convertedFromQuote
    },
    isDenied() {
      return this.order.status == OrderStatusTypes.denied
    },
    isApproved() {
      if(this.isOrderPendingApproval)
        return false
      var productionApproved = !this.order.requiresProductionApproval || this.getApprovalStatusForType(OrderApprovalTypes.production) == ApprovalTypes.approved
      var pricingApproved = !this.order.requiresPricingApproval || this.getApprovalStatusForType(OrderApprovalTypes.pricing) == ApprovalTypes.approved
      var customerApproved = !this.order.requiresCustomerApproval || this.getApprovalStatusForType(OrderApprovalTypes.customer) == ApprovalTypes.approved
      var accountsApproved = !this.order.requiresAccountsApproval || this.getApprovalStatusForType(OrderApprovalTypes.account) == ApprovalTypes.approved

      return productionApproved && pricingApproved && customerApproved && accountsApproved
    },
    isAccountsPricingApproved() {
      if(this.order.status == OrderStatusTypes.pendingAccountApproval)
        return false

      var pricingApproved = !this.order.requiresPricingApproval || this.getApprovalStatusForType(OrderApprovalTypes.pricing) == ApprovalTypes.approved
      var accountsApproved = !this.order.requiresAccountsApproval || this.getApprovalStatusForType(OrderApprovalTypes.account) == ApprovalTypes.approved

      return pricingApproved && accountsApproved
    },
    isProductionApproved() {
      if(this.order.status == OrderStatusTypes.pendingProductionApproval)
        return false

      var productionApproved = !this.order.requiresProductionApproval || this.getApprovalStatusForType(OrderApprovalTypes.production) == ApprovalTypes.approved

      return productionApproved
    },
    isCustomerApproved() {
      if(this.order.status == OrderStatusTypes.pendingCustomerApproval)
        return false

      var customerApproved = !this.order.requiresCustomerApproval || this.getApprovalStatusForType(OrderApprovalTypes.customer) == ApprovalTypes.approved

      return customerApproved
    },
    deniedApproval() {
      var productionDenied = this.order.requiresProductionApproval && this.getApprovalStatusForType(OrderApprovalTypes.production) == ApprovalTypes.denied
      var pricingDenied = this.order.requiresPricingApproval && this.getApprovalStatusForType(OrderApprovalTypes.pricing) == ApprovalTypes.denied
      var customerDenied = this.order.requiresCustomerApproval && this.getApprovalStatusForType(OrderApprovalTypes.customer) == ApprovalTypes.denied
      var accountsDenied = this.order.requiresAccountsApproval && this.getApprovalStatusForType(OrderApprovalTypes.account) == ApprovalTypes.denied

      return productionDenied || pricingDenied || customerDenied || accountsDenied
    },
    requiresApproval() {
      return this.order.requiresProductionApproval || this.order.requiresPricingApproval || this.order.requiresCustomerApproval || this.order.requiresAccountsApproval
    },
    requiresAccountsApproval() {
      return this.order.requiresPricingApproval || this.order.requiresAccountsApproval
    },
    requiresProductionApproval() {
      return this.order.requiresProductionApproval
    },
    requiresCustomerApproval() {
      return this.order.requiresCustomerApproval
    },
    externalApprovalsAvailable() {
      if(this.order.status != OrderStatusTypes.pendingExternalSetup)
        return false

      return true
    },
    customerApprovalAvailable() {
      // Pricing & production approvals need to be approved before we can deal with customer approval
      if(this.order.status != OrderStatusTypes.pendingApproval)
        return false

      var productionApproved = !this.order.requiresProductionApproval || this.getApprovalStatusForType(OrderApprovalTypes.production) == ApprovalTypes.approved
      var pricingApproved = !this.order.requiresPricingApproval || this.getApprovalStatusForType(OrderApprovalTypes.pricing) == ApprovalTypes.approved

      return productionApproved && pricingApproved
    },
    canHoldOrder() {
      if(this.order.type == OrderTypes.quote)
        return false
      if(this.order.status == OrderStatusTypes.completed ||
        this.order.status == OrderStatusTypes.denied ||
        this.order.status == OrderStatusTypes.cancelled)
        return false
      if(this.order.isOnHold)
        return false
      return true
    },
    canUnHoldOrder() {
      if(!this.order.isOnHold)
        return false
      if(this.order.status == OrderStatusTypes.completed ||
        this.order.status == OrderStatusTypes.denied ||
        this.order.status == OrderStatusTypes.cancelled)
        return false
      if(this.order.isAccountsHold && !this.userHasUpdatePermission(this.PermissionTypes.OrderPermissionTypes.orderAccountsApproval))
        return false
      if(this.itemList.editing)
        return false
      return true
    },
    canCancelOrder() {
      if(this.order.status == OrderStatusTypes.cancelled ||
         this.order.status == OrderStatusTypes.completed)
        return false
      if(this.order.isInvoiced)
        return false
      if(this.itemList.editing)
        return false
      return true
    },
    canRestoreOrder() {
      return this.order.status == OrderStatusTypes.denied
    },
    canRecheckApprovals() {
      return this.order.status == OrderStatusTypes.pendingAccountApproval ||
             this.order.status == OrderStatusTypes.pendingProductionApproval ||
             this.order.status == OrderStatusTypes.pendingCustomerApproval
    },
    canRecheckPurchaseOrders() {
      return this.order.status == OrderStatusTypes.pendingExternalSetup
    },
    canSkipPrePaid() {
      return this.order.status == OrderStatusTypes.pendingPrePaid
    },
    canSkipCashOnDelivery() {
      return this.order.status == OrderStatusTypes.pendingCODPaid
    },
    canForceDespatch() {
      if(this.order.status == OrderStatusTypes.pendingCODPaid) {
        return this.userHasUpdatePermission(this.PermissionTypes.OrderPermissionTypes.orderAccountsApproval)
      }
      return this.order.status == OrderStatusTypes.despatch
    },
    canForceInvoice() {
      if(this.isQuote || this.isNCR)
        return false
      if(this.order.isInvoicingComplete)
        return false
      
      return this.order.status == OrderStatusTypes.despatch || this.order.status == OrderStatusTypes.pendingCODPaid
    },
    canRevertDespatch() {
      if(!this.order.requiresProduction)
        return false
      if(this.order.status == OrderStatusTypes.pendingCODPaid || this.order.status == OrderStatusTypes.despatch)
        return this.userHasUpdatePermission(this.PermissionTypes.OrderPermissionTypes.orderAdministrator)
      return false
    },
    canCreateNCR() {
      if(this.order.status == OrderStatusTypes.production ||
        this.order.status == OrderStatusTypes.despatch ||
        this.order.status == OrderStatusTypes.completed)
        return true

      return false
    },
    ncrItemList() {
      if(!this.itemList || !this.itemList.itemData)
        return []
      return this.itemList.itemData.filter(item => item.orderApproval != null)
    },

    isOrderStartedProduction() {
      if(this.order.status == OrderStatusTypes.production ||
        this.order.status == OrderStatusTypes.despatch ||
        this.order.status == OrderStatusTypes.pendingCODPaid ||
        this.order.status == OrderStatusTypes.completed)
        return true

      return false
    },
    isOrderInPreProduction() {
      return this.order.status == OrderStatusTypes.preProduction
    },
    canRecheckOrderProcessing() {
      return this.order.status == OrderStatusTypes.processing
    },
    canRecheckOrderProduction() {
      if(!this.userHasUpdatePermission(this.PermissionTypes.OrderPermissionTypes.orderAdministrator))
        return false

      return this.order.status == OrderStatusTypes.production
    },
    canAddGlassOrderItems() {
      // JB: 04/10/2023 - Cannot add new items to an order fully invoiced
      // This breaks some items like delivery that use percent of order
      if(this.order.isInvoicingComplete)
        return false

      // JB: 15/01/2020 - Can add glass/template items if the user has order admin permissions
      if(this.userHasUpdatePermission(this.PermissionTypes.OrderPermissionTypes.orderAdministrator))
        return true

      // JB: 20/07/2020 - Can add glass/template items up until production
      // General items always available to add
      // FUTURE: May need to limit if pre-paid
      if(this.isOrderStartedProduction)
        return false

      return true
    },
    canAddGeneralOrderItems() {
      // JB: 04/10/2023 - Cannot add new items to an order fully invoiced
      // This breaks some items like delivery that use percent of order
      if(this.order.isInvoicingComplete)
        return false

      return true
    },
    isOptionOrderEditorAvailable() {
      return this.userHasUpdatePermission(this.PermissionTypes.OrderEditorPermissionTypes.orderEditor)
    },
    isOptionCustomerApprovalAvailable() {
      // KC: Once order has started production, don't allow customer approval option
      if(this.itemList.editing && this.isOrderStartedProduction)
        return false

      return true
    },

    canSendOrder() {
      if(!this.isOrderPendingApproval && !this.isQuote && !this.isOrderCancelledDenied && !this.isOrderCompleted && !this.isStatusDraft)
        return true
      return false
    },
    canCopyOrder() {
      if(!this.userHasCreatePermission(this.PermissionTypes.OrderPermissionTypes.order))
        return false
      if(!this.isOrderPendingApproval)
        return true
      return false
    },
    canPrintSheet() {
      if(!this.isOrderPendingApproval && !this.isStatusDraft && !this.isStatusPendingPrePaid)
        return true
      return false
    },
    canPrintProductionLabels() {
      if((this.isOrderInPreProduction || this.isOrderStartedProduction) && !this.isOrderCancelledDenied && !this.isOrderCompleted && !this.isStatusPendingPrePaid)
        return true
      return false
    },
    hasLinkedNCROrders() {
      return this.ncrOrderMapList.length > 0
    },
    orderTypeTitle() {
      return Utils.getLookupName(OrderTypeLookup, this.order.type)
    },
    orderTitle() {
      var title = Utils.getLookupName(OrderTypeLookup, this.order.type)
      if(this.order.sequenceNumber != null)
        title += ` #${this.order.sequenceNumber}`
      return title
    },
    addressDeliveryLabelList() {
      if(!this.isDespatchDelivery)
        return []
      if(this.order.deliveryTag)
        return this.order.deliveryTag
      return []
    },
    isPreProductionEnabled() {
      return parseInt(this.accountSettings[AccountSettingKeys.orderEnablePreProduction] || 0) != 0
    },
    activityStreamURL() {
      if(!this.$data.orderId)
        return null
      return `/orders/${this.$data.orderId}/activitystream`
    },
    despatchProgressTitle() {
      if(this.order.despatchTaskType)
        return this.order.despatchTaskType.name
      return 'Despatch'
    },
    canAddDespatchTask() {
      if(!this.userHasCreatePermission(this.PermissionTypes.TaskPermissionTypes.task))
        return false

      // If in production, can only add tasks if not pending COD order account type
      // Done for pickups of panels that are ready
      if(this.isOrderInProduction && !this.isOrderPaymentTypeCOD)
        return true
      
      // Always if in despatch
      return this.isOrderInDespatch
    }
  },
  methods: {
    reloadData() {
      if(this.$refs.activityStream)
        this.$refs.activityStream.reloadData()
      if(this.$refs.orderDespatchList)
        this.$refs.orderDespatchList.reloadData()
      if(this.$refs.orderInvoiceList)
        this.$refs.orderInvoiceList.reloadData()
      if(this.$refs.orderPrePaidPaymentList)
        this.$refs.orderPrePaidPaymentList.reloadData()
      if(this.$refs.orderCODPaymentList)
        this.$refs.orderCODPaymentList.reloadData()
      if(this.$refs.recentComLog)
        this.$refs.recentComLog.reloadData()
      if(this.$refs.prePaidActivityLogEventList)
        this.$refs.prePaidActivityLogEventList.reloadData()
      if(this.$refs.codActivityLogEventList)
        this.$refs.codActivityLogEventList.reloadData()

      this.requestData()
    },
    requestData() {
      this.pageLoadData(Promise.all([
        this.$apiService.ordersService.getOrder(this.$data.orderId),
        this.$apiService.orderEditorService.getOrderEditorEdgeworkList({ include: 'orderEditorEdgeworkStock,orderEditorEdgeworkAllowance' }),
        this.$apiService.ordersService.getStampConfig()
      ]))
      .then(results => {
        this.order = results[0].data
        this.edgeworkConfigList = results[1].data
        this.orderStampConfig = results[2].data

        // Copy the order item list & modify for display/editing
        this.itemList.itemData = this.buildOrderItemList(this.order.orderItems)

        // Build the NCR order map into a displayable list
        this.ncrOrderMapList = this.buildNCROrderList(this.order.ncrOrderMap)

        // If this is a quote just created & available, popup the send quote dialog
        // Part of the flow of sending a quote straight after creation
        if(this.isQuote && this.order.status == OrderStatusTypes.quotePendingSent && this.$data.sendQuoteOnLoad) {
          this.$nextTick(() => {
            this.sendQuote()
          })
        }
        this.$data.sendQuoteOnLoad = false
      })
      .catch(err => this.handlePageLoadError(err))
    },

    onBeforeUnload: function(event) {
      if(!this.itemList.editing)
        return null

      var confirmationMessage = `You have unsaved changes while editing items. Are you sure you want to exit?`

      event.returnValue = confirmationMessage
      return confirmationMessage
    },

    getApprovalStatusForType(type) {
      if(!this.order || !this.order.orderApprovals)
        return ApprovalTypes.pending
      let approval = this.order.orderApprovals.find(app => app.type == type)
      if(!approval)
        return ApprovalTypes.pending
      return approval.approvalStatus
    },
    isApprovalTypeApproved(type) {
      return this.getApprovalStatusForType(type) == ApprovalTypes.approved
    },
    isApprovalTypeDenied(type) {
      return this.getApprovalStatusForType(type) == ApprovalTypes.denied
    },
    recheckApprovals() {
      return this.$apiService.ordersService.recheckApprovals(this.$data.orderId)
      .then(() => {
        this.successToast(`Approvals rechecked`)
        this.reloadData()
      })
      .catch(err => {
        this.displayNetworkError(err, 'Failed to recheck approvals', 'Error while attempting to recheck approvals')
      })
    },
    recheckPurchaseOrders() {
      return this.$apiService.ordersService.recheckExternalPurchaseOrders(this.$data.orderId)
      .then(() => {
        this.successToast(`Purchase Orders rechecked`)
        this.reloadData()
      })
      .catch(err => {
        this.displayNetworkError(err, 'Failed to recheck purchase orders', 'Error while attempting to recheck purchase orders')
      })
    },

    approveOrder(approvalType, title) {

      this.displayModal(OrderActionDialog, { 
        title: `Approve ${title}`,
        icon: 'fa-thumbs-up',
        submitTitle: `Approve ${title}`,
        textLabel: 'Approval Note',
        placeholder: `Notes for approving the ${title} section of the order`,
        textField: 'approvedNote',
        submitPromise: (data) => {
          return this.$apiService.ordersService.approveOrder(this.$data.orderId, { approvalType: approvalType, approvedNote: data.text, uploadFileList: data.uploadFileList })
        }
      })
      .then((success) => {
        if(success) {
          this.successToast(`${title} approved`)
          this.reloadData()
        }
      })
    },
    denyOrder(approvalType, title) {
      this.displayModal(OrderActionDialog, { 
        title: `Deny ${title}`,
        icon: 'fa-thumbs-down',
        submitTitle: `Deny ${title}`,
        textLabel: 'Denied Reason',
        placeholder: `Reason for denying the ${title} section of the order`,
        textField: 'deniedReason',
        submitPromise: (data) => {
          return this.$apiService.ordersService.denyOrder(this.$data.orderId, { approvalType: approvalType, deniedReason: data.text, uploadFileList: data.uploadFileList })
        }
      })
      .then((success) => {
        if(success) {
          this.successToast(`${title} denied`)
          this.reloadData()
        }
      })
    },

    skipPrePaid() {
      this.displayModal(OrderActionDialog, { 
        title: `Skip Pre-Paid`,
        icon: 'fa-forward',
        notes: 'Move the order straight into production/despatch without payment of pre-paid?',
        showAttachments: false,
        submitTitle: `Skip Prepaid`,
        textLabel: 'Reason',
        placeholder: `Reason for skipping pre-payment and forcing to production/despatch`,
        textField: 'reason',
        submitPromise: (data) => {
          return this.$apiService.ordersService.skipPendingPayments(this.$data.orderId, { reason: data.text })
        }
      })
      .then((success) => {
        if(success) {
          this.successToast(`Pre-payment Skipped`)
          this.reloadData()
        }
      })
    },
    skipCashOnDelivery() {
      this.displayModal(OrderActionDialog, { 
        title: `Skip Cash On Delivery`,
        icon: 'fa-forward',
        notes: 'Move the order straight into despatch without payment of the cash on delivery?',
        showAttachments: false,
        submitTitle: `Skip COD`,
        textLabel: 'Reason',
        placeholder: `Reason for skipping cash on delivery and forcing to despatch`,
        textField: 'reason',
        submitPromise: (data) => {
          return this.$apiService.ordersService.skipPendingPayments(this.$data.orderId, { reason: data.text })
        }
      })
      .then((success) => {
        if(success) {
          this.successToast(`Cash On Delivery Skipped`)
          this.reloadData()
        }
      })
    },
    forceOrderDespatched() {
      this.displayModal(OrderActionDialog, { 
        title: `Force Order Despatched`,
        icon: 'fa-shipping-fast',
        notes: 'Force the order to be fully despatched and completed?',
        showAttachments: false,
        submitTitle: `Force Despatch`,
        textLabel: 'Reason',
        placeholder: `Reason for forcing despatch`,
        textField: 'reason',
        submitPromise: (data) => {
          return this.$apiService.ordersService.forceOrderDespatched(this.$data.orderId, { reason: data.text })
        }
      })
      .then((success) => {
        if(success) {
          this.successToast(`Order Despatched`)
          this.reloadData()
        }
      })
    },
    forceOrderInvoiced() {
      this.displayModal(OrderActionDialog, { 
        title: `Force Order Invoiced`,
        icon: 'fa-envelope-open-dollar',
        notes: 'Force the order to be fully invoiced? This will not despatch the order',
        showAttachments: false,
        submitTitle: `Force Invoice`,
        textLabel: 'Reason',
        placeholder: `Reason for forcing order to be invoiced`,
        textField: 'reason',
        submitPromise: (data) => {
          return this.$apiService.ordersService.forceOrderInvoiced(this.$data.orderId, { reason: data.text })
        }
      })
      .then((success) => {
        if(success) {
          this.successToast(`Order Invoiced`)
          this.reloadData()
        }
      })
    },

    startExternalItemsSetup() {
      this.$router.push(`/orders/${this.$data.orderId}/externalsetup`)
    },
    emailExternalPurchaseOrder(purchaseOrderId) {
      if(!this.order || !this.order.purchaseOrders)
        return
      let purchaseOrder = this.order.purchaseOrders.find(po => po.id == purchaseOrderId)
      if(!purchaseOrder)
        return

      this.displayModal(SendExternalPurchaseOrderDialog, { 
        orderId: parseInt(this.$data.orderId),
        purchaseOrderId: parseInt(purchaseOrderId),
        supplierContactId: purchaseOrder.contactId
      })
      .then((success) => {
        if(success) {
          this.successToast('Purchase Order Emailed')
          this.reloadData()
        }
      })
    },

    restoreOrder() {
      this.displayModal(OrderActionDialog, { 
        title: `Restore ${this.orderTypeTitle}`,
        icon: 'fa-undo',
        submitTitle: `Restore ${this.orderTypeTitle}`,
        textLabel: 'Restore Reason',
        placeholder: `Reason for restoring`,
        textField: 'reason',
        notes: `This will restore the ${this.orderTypeTitle} back to pending and will require any approvals to be re-approved`,
        submitPromise: (data) => {
          return this.$apiService.ordersService.restoreOrder(this.$data.orderId, { reason: data.text, uploadFileList: data.uploadFileList })
        }
      })
      .then((success) => {
        if(success) {
          this.successToast(`${this.orderTypeTitle} restored`)
          this.reloadData()
        }
      })
    },


    // Non Chargeable Rework Methods
    createNCR() {
      // Copy the order to the OrderNew page & flag as NCR
      this.$router.push({
        name: 'OrderNew',
        params: {
          NCRFromOrder: true,
          sourceOrderId: parseInt(this.$data.orderId)
        }
      })
    },
    acceptNCRItem(orderItem) {
      var itemTitle = `${orderItem.stock.code} - ${orderItem.glassHeight}x${orderItem.glassWidth}`

      this.displayModal(OrderActionDialog, { 
        title: `Approve ${itemTitle} NCR`,
        icon: 'fa-thumbs-up',
        notes: 'Note: This will accept all units in the item',
        submitTitle: 'Approve Item',
        textLabel: 'Approval Note',
        placeholder: 'Notes for approving the NCR for this item',
        textField: 'approvedNote',
        submitPromise: (data) => {
          return this.$apiService.ordersService.approveOrderItemNCR(this.$data.orderId, orderItem.id, {
            approvedNote: data.text,
            uploadFileList: data.uploadFileList
          })
        }
      })
      .then((success) => {
        if(success) {
          this.successToast(`Item NCR accepted`)
          this.reloadData()

          // Check if the NCR is now completed. If so, display the results
          this.checkNCRCompleted()
        }
      })
    },
    rejectNCRItem(orderItem) {
      var itemTitle = `${orderItem.stock.code} - ${orderItem.glassHeight}x${orderItem.glassWidth}`

      this.displayModal(OrderDenyNCRDialog, { 
        title: `Deny ${itemTitle} NCR`,
        icon: 'fa-thumbs-down',
        submitTitle: 'Deny',
        showQuantity: true,
        quantityLabel: 'Rejected Quantity',
        quantitySuffix: ` / ${orderItem.quantity}`,
        quantityNote: 'Any remaining item quantity will be approved',
        deliveryFee: this.order.ncrAdditionalDeliveryCharge,
        textLabel: 'Denied Note',
        placeholder: 'Notes for denying the NCR for this item',
        textField: 'deniedReason',
        submitPromise: (data) => {
          return this.$apiService.ordersService.denyOrderItemNCR(this.$data.orderId, orderItem.id, {
            deniedReason: data.text,
            rejectedQuantity: data.quantity,
            ncrAdditionalDeliveryCharge: data.ncrDeliveryFeeAmount,
            uploadFileList: data.uploadFileList
          })
        }
      })
      .then((success) => {
        if(success) {
          this.successToast(`Item NCR rejected`)
          this.reloadData()

          // Check if the NCR is now completed. If so, display the results
          this.checkNCRCompleted()
        }
      })
    },
    displayNCRPopover(event, orderItem) {
      this.$refs.ncrPopover.itemData = orderItem
      this.$refs.ncrPopover.displayFromElement(event.target)
    },
    // Check if the NCR order is now completed
    // If so, display details about the resulting invoice/credit notes
    // Only call this when approving/denying NCRd items
    checkNCRCompleted() {
      this.$apiService.ordersService.getOrder(this.$data.orderId)
      .then(result => {
        let orderInfo = result.data
        if(orderInfo.status == OrderStatusTypes.completed) {
          this.displayNCRAccounts()
        }
      })
      .catch(err => {
        console.error(err)
      })
    },
    displayNCRAccounts() {
      this.displayModal(OrderNCRAccountsDialog, {
        orderId: parseInt(this.$data.orderId)        
      })
      .then(() => {
        // Reload the invoice list if sent flag updated from dialog
        this.reloadData()
      })
    },

    isStatusCompleted(status) {
      // Must be in order
      let statusList = [
        OrderStatusTypes.draft,
        OrderStatusTypes.pendingAccountApproval,
        OrderStatusTypes.pendingProductionApproval,
        OrderStatusTypes.quotePendingSent,
        OrderStatusTypes.pendingCustomerApproval,
        OrderStatusTypes.pendingPrePaid,
        OrderStatusTypes.pendingExternalSetup,
        OrderStatusTypes.preProduction,
        OrderStatusTypes.production,
        OrderStatusTypes.pendingCODPaid,
        OrderStatusTypes.onHold,
        OrderStatusTypes.despatch,
        OrderStatusTypes.ncrPendingReturn,
        OrderStatusTypes.completed,
        OrderStatusTypes.cancelled,
      ]

      return statusList.indexOf(this.order.status) >= statusList.indexOf(status)
    },

    displaySummaryTab(evt) {
      this.$refs.summaryTab.show()
    },
    displayDetailsTab(evt) {
      this.$refs.detailsTab.show()
    },
    displayProductionTab() {
      this.$refs.productionTab.show()
    },
    displayOrderReport(options = {}) {
      var params = $.param(options)
      this.displayReportPDF(`/orders/${this.$data.orderId}/report?${params}`)
    },
    downloadProductionSheets(fromChecklist = false, hideInternal = false) {
      // COD/Prepaid/Account status
      // Used to tell desktop app what printer to send production sheets too
      var printParams = { download: this.isDesktopAppMode(), orderproduction: true, production: true, hideinternal: hideInternal, logchecklist: fromChecklist }

      if(this.order.contact.contactCreditInfo) {
        if(this.order.contact.contactCreditInfo.creditType == CreditTypes.account)
          printParams.creditaccount = true
        if(this.order.contact.contactCreditInfo.creditType == CreditTypes.prepaid)
          printParams.creditprepaid = true
        if(this.order.contact.contactCreditInfo.creditType == CreditTypes.cod)
          printParams.creditcod = true
      }
      if(this.order.type == OrderTypes.nonChargeableRework)
        printParams.orderncr = true

      var params = $.param(printParams)
      this.$apiService.reportingService.generateReportSignedURL(`/reporting/orders/${this.$data.orderId}/report?${params}`)
      .then(response => {
        window.location.href = response.data.url
      })
    },
    displayOrderPanelLabels(filterPanelId = null, fromChecklist = false) {
      var labelURL = `/orders/${this.$data.orderId}/panellabels`
      if(filterPanelId != null)
        labelURL = `/orders/${this.$data.orderId}/panellabels/${filterPanelId}`

      var printParams = { download: this.isDesktopAppMode(), label: true, logchecklist: fromChecklist }

      var params = $.param(printParams)
      this.$apiService.reportingService.generateReportSignedURL(`${labelURL}?${params}`)
      .then(response => {
        window.location.href = response.data.url
      })
    },

    editOrder() {
      this.displayModal(EditOrderDialog, { 
        orderId: parseInt(this.$data.orderId),
      }, this.order)
      .then((success) => {
        if(success) {
          this.successToast('Order Updated')
          this.reloadData()
        } else {
          // Reload even if dialog cancelled as the order has been placed on hold during editing
          this.reloadData()
        }
      })
    },
    editOrderDespatch() {
      this.displayModal(EditOrderDespatchDialog, { 
        orderId: parseInt(this.$data.orderId),
      }, this.order)
      .then((success) => {
        if(success) {
          this.successToast('Order Despatch Updated')
          this.reloadData()
        } else {
          // Reload even if dialog cancelled as the order has been placed on hold during editing
          this.reloadData()
        }
      })
    },
    editOrderItemList() {
      // Place a hold on the order while editing
      // Ignore for quotes / draft
      if(this.isQuote || this.order.status == OrderStatusTypes.draft) {
        this.itemList.editing = true
        return
      }

      this.displayProcessingDialog({
        title: 'Placing hold on order while editing',
        message: 'The order needs to be on hold while editing',
        icon: 'fa-pause',
        onBeforeOpen: (instance) => {
          this.$apiService.ordersService.holdOrder(this.$data.orderId, { holdType: OrderHoldTypes.orderEditing, reason: 'Editing Order Item List' })
          .then(() => {
          })
          .finally(() => {
            instance.hide()
            this.itemList.editing = true
          })
        }
      })
    },
    itemCancel(orderItemId) {
      this.displayModal(CancelOrderItemDialog, { 
        order: this.order,
        orderId: parseInt(this.$data.orderId),
        orderItemId: parseInt(orderItemId),
      })
      .then((success) => {
        if(success) {
          this.successToast('Order Item Cancelled')
          this.reloadData()
        }
      })
    },
    forceProductionItemListUpdate() {
      var message = 'This will override production warnings and will force the update of the order items'
      this.confirmMessage('Force Order Item Update', '', { html: message })
      .then(confirm => {
        if(confirm) {
          this.submitItemList(true)
        }
      })
    },
    submitItemList(orderAdminForceUpdate = false) {
      this.itemList.form = this.clearFormResponse()
      this.itemList.itemData = this.$refs.orderItemList.getItemList()
      this.itemList.forceProductionUpdateAvailable = false

      // Generate the edgework details & standard item diagrams as required & prepare the list for updating
      this.generateOrderItemStandardEdgeworkDetails(this.itemList.itemData, this.edgeworkConfigList)
      this.generateOrderItemStandardDiagrams(this.itemList.itemData, this.orderStampConfig)
      let updateItemList = this.prepareOrderItemListForUpdate(this.itemList.itemData, { cleanupStructures: true })

      let sendUpdateOrderItemList = (resetExternalPurchaseOrders = false) => {
        this.itemList.submitting = true

        let updateData = {
          orderItemList: updateItemList,
          resetExternalPurchaseOrders: resetExternalPurchaseOrders,
          orderAdminForceUpdate: orderAdminForceUpdate
        }
        this.$apiService.ordersService.updateOrderItems(this.$data.orderId, updateData, { useCompression: true })
        .then(response => {
          this.itemList.editing = false
          this.successToast('Order Item List Updated')
          this.reloadData()

          // If reset POs set, go to first tab
          if(resetExternalPurchaseOrders) {
            this.displaySummaryTab()
          }

        })
        .catch(err => {
          this.$data.itemList.form = this.processFormResponse(err)

          // Check options if we can show option to force the update
          let responseInfo = this.$data.itemList.form.errorData
          if(responseInfo && !!responseInfo.isForceUpdateAvailable) {
            this.itemList.forceProductionUpdateAvailable = true            
          }
        })
        .finally(() => {
          this.$data.itemList.submitting = false
        })
      }


      // If the order has external purchase orders associated with it, present a dialog to handle options with external setup
      let requiresExternalUpdate = false
      if(this.order && _.isArray(this.order.purchaseOrders)) {
        let activePurchaseOrders = this.order.purchaseOrders.filter(purchaseOrder => purchaseOrder.status != PurchaseOrderStatusTypes.cancelled)
        if(activePurchaseOrders.length > 0)
          requiresExternalUpdate = true
      }

      if(requiresExternalUpdate) {
        this.displayModal(OrderExternalItemUpdateDialog, { 
          orderId: parseInt(this.$data.orderId),
        })
        .then((externalUpdateType) => {
          if(externalUpdateType) {
            // Update order item list
            sendUpdateOrderItemList(externalUpdateType == OrderItemExternalUpdateTypes.resetExternalSetup)
          }
        })

      } else {
        // Regular update
        sendUpdateOrderItemList()        
      }
    },
    cancelEditingItemList() {
      this.itemList.editing = false
      this.itemList.form = this.clearFormResponse()
      this.reloadData()
    },
    startOrderEditor() {
      this.showOrderEditor = true

      this.itemList.itemData = this.$refs.orderItemList.getItemList()
    },
    finishOrderEditor() {
      this.showOrderEditor = false

      if(this.itemList.editing) {
        this.$refs.orderItemList.copyOrderItemList(_.cloneDeep(this.itemList.itemData), null, -1, { isEditorItemCopy: true })
      }
      else
        this.itemList.itemData = this.$refs.orderItemList.getItemList()
    },
    startOrderEditorViewer() {
      this.showOrderEditor = true
      this.itemList.editing = false

      this.itemList.itemData = this.$refs.orderItemList.getItemList()
    },

    // Draft
    submitDraftOrder() {
      this.confirmMessage('Approve Draft?', `Approve the draft? This will push the ${this.orderTypeTitle} into the system for approval/processing`)
      .then(success => {
        if(success) {
          this.$apiService.ordersService.approveDraft(this.$data.orderId)
          .then(response => {
            this.successToast('Draft Approved')
            this.reloadData()
          })
          .catch(err => {
            this.displayNetworkError(err, 'Failed to approve draft', 'Error while attempting to approve the draft')            
          })
        }
      })
    },

    sendOrder() {
      this.displayModal(SendOrderDialog, { 
        orderId: parseInt(this.$data.orderId),
        contactId: parseInt(this.order.contactId),
        customContactEmail: this.order.customContactEmail,
        defaultContactPersonId: parseInt(this.order.contactPersonId)
      })
      .then((success) => {
        if(success) {
          this.successToast('Order Sent')
          this.reloadData()
        }
      })
    },


    // Quote Methods
    sendQuote() {
      this.displayModal(SendQuoteDialog, { 
        orderId: parseInt(this.$data.orderId),
        contactId: parseInt(this.order.contactId),
        customContactEmail: this.order.customContactEmail,
      })
      .then((success) => {
        if(success) {
          this.successToast('Quote Sent')
          this.reloadData()
        }
      })
    },
    quoteApproved() {
      this.confirmMessage('Approve Quote?', 'Approve the quote? This will convert it into an order and move it into production/despatch')
      .then(success => {
        if(success) {
          this.$apiService.ordersService.approveQuote(this.$data.orderId)
          .then(response => {
            this.successToast('Quote Approved')
            this.reloadData()
          })
          .catch(err => {
            this.displayNetworkError(err, 'Failed to approve quote', 'Error while attempting to approve the quote')            
          })
        }
      })
    },
    cancelQuote() {
      this.displayModal(OrderActionDialog, { 
        title: 'Cancel Quote',
        icon: 'fa-times',
        submitTitle: 'Cancel Quote',
        textLabel: 'Cancel Reason',
        placeholder: 'Enter the reason for cancelling the quote',
        textField: 'reason',
        submitPromise: (data) => {
          return this.$apiService.ordersService.cancelQuote(this.$data.orderId, { reason: data.text, uploadFileList: data.uploadFileList })
        }
      })
      .then((success) => {
        if(success) {
          this.successToast('Quote Cancelled')
          this.reloadData()
        }
      })
    },

    // Hold Methods
    holdOrder() {
      let typeTitle = this.orderTypeTitle

      this.displayModal(OrderHoldDialog, { 
        title: `Hold ${typeTitle}`,
        submitTitle: `Hold ${typeTitle}`,
        submitPromise: (data) => {
          return this.$apiService.ordersService.holdOrder(this.$data.orderId, { holdType: data.holdType, reason: data.text, uploadFileList: data.uploadFileList })
        }
      })
      .then((success) => {
        if(success) {
          this.successToast(`${typeTitle} Held`)
          this.reloadData()
        }
      })
    },
    unHoldOrder() {
      let typeTitle = this.orderTypeTitle

      this.displayModal(OrderActionDialog, { 
        title: `Resume ${typeTitle}`,
        icon: 'fa-play',
        submitTitle: `Resume ${typeTitle}`,
        textLabel: 'Resume Reason',
        placeholder: 'Enter the reason for removing the hold and resuming the order',
        textField: 'reason',
        submitPromise: (data) => {
          return this.$apiService.ordersService.unHoldOrder(this.$data.orderId, { reason: data.text, uploadFileList: data.uploadFileList })
        }
      })
      .then((success) => {
        if(success) {
          this.successToast(`${typeTitle} Hold Removed`)
          this.reloadData()
        }
      })
    },
    cancelOrder() {
      let typeTitle = this.orderTypeTitle

      this.displayModal(CancelOrderDialog, {
        order: this.$data.order,
        orderId: parseInt(this.$data.orderId)
      })
      .then((success) => {
        if(success) {
          this.successToast(`${typeTitle} Cancelled`)
          this.reloadData()
        }
      })
    },

    // Copy the order into a new order
    copyOrder() {
      this.$router.push({
        name: 'OrderNew',
        params: {
          copyFromOrder: true,
          sourceOrderId: parseInt(this.$data.orderId)
        }
      })
    },

    // Add a direct payment to the order
    // For pre-paid / cash on delivery orders
    addPayment() {
      this.displayModal(AddOrderPaymentDialog, {
        orderId: parseInt(this.$data.orderId)
      })
      .then((success) => {
        if(success) {
          this.successToast(`Payment Received`)
          this.reloadData()
        }
      })
    },

    // Skip a checklist item
    skipChecklistItem(itemType) {
      let title = Utils.getLookupName(OrderChecklistItemTypeLookup, itemType)

      this.displayModal(OrderActionDialog, { 
        title: `Skip ${title}`,
        icon: 'fa-fast-forward',
        submitTitle: `Skip`,
        textLabel: 'Skip Reason',
        placeholder: `Reason for skipping ${title}`,
        textField: 'skipReason',
        showAttachments: false,
        submitPromise: (data) => {
          return this.$apiService.ordersService.skipChecklistItem(this.$data.orderId, { type: itemType, skipReason: data.text })
        }
      })
      .then((success) => {
        if(success) {
          this.successToast(`${title} skipped`)
          this.reloadData()
        }
      })
    },

    sysAdminForceSkipPreProduction() {
      this.displayModal(OrderActionDialog, { 
        title: `Skip Pre Production`,
        icon: 'fa-tools',
        notes: 'Automatically skips all pre production steps',
        submitTitle: `Skip`,
        textLabel: 'Skip Reason',
        placeholder: `Reason for skipping pre production`,
        textField: 'reason',
        showAttachments: false,
        submitPromise: (data) => {
          return this.$apiService.ordersService.orderForceSkipPreProduction(this.$data.orderId, { reason: data.text })
        }
      })
      .then((success) => {
        if(success) {
          this.successToast(`Pre Production completed`)
          this.reloadData()
        }
      })
    },
    sysAdminForceCompleteProduction() {
      this.displayModal(OrderActionDialog, { 
        title: `Force Production Completed`,
        icon: 'fa-tools',
        notes: 'Automatically forces completion of all pending panel production tasks',
        warning: 'This will complete all stages and will not register compliance details (eg, toughening batches)',
        submitTitle: `Complete`,
        textLabel: 'Completion Reason',
        placeholder: `Reason for forcing production complete`,
        textField: 'reason',
        showAttachments: false,
        submitPromise: (data) => {
          return this.$apiService.ordersService.orderForceProductionComplete(this.$data.orderId, { reason: data.text })
        }
      })
      .then((success) => {
        if(success) {
          this.successToast(`Production completed`)
          this.reloadData()
        }
      })
    },
    orderAdminRevertDespatch() {
      this.displayModal(OrderActionDialog, { 
        title: `Revert Despatch`,
        icon: 'fa-undo',
        notes: 'Reverts the order back to production for any order adjustments',
        submitTitle: `Revert`,
        textLabel: 'Revert Reason',
        placeholder: `Reason for reverting despatch`,
        textField: 'reason',
        showAttachments: false,
        submitPromise: (data) => {
          return this.$apiService.ordersService.orderRevertDespatch(this.$data.orderId, { reason: data.text })
        }
      })
      .then((success) => {
        if(success) {
          this.successToast(`Despatch Reverted`)
          this.reloadData()
        }
      })
    },
    orderAdminRecheckProduction() {
      this.$apiService.ordersService.orderForceRecheckProduction(this.$data.orderId)
      .then(() => {
        this.successToast(`Recheck Completed`)
        this.reloadData()
      })
      .catch(err => {
        this.displayNetworkError(err, 'Failed to recheck production', 'Error while attempting to recheck production')
      })
    },
    sysAdminRecheckProcessing() {
      this.$apiService.ordersService.orderForceRecheckProcessing(this.$data.orderId)
      .then(() => {
        this.successToast(`Order processing`)
        this.reloadData()
      })
      .catch(err => {
        this.displayNetworkError(err, 'Failed to recheck processing', 'Error while attempting to recheck processing')
      })
    },

    buildNCROrderList(ncrOrderMap, sourceList = [], level = 0) {
      if(!_.isArray(ncrOrderMap))
        return sourceList

      ncrOrderMap.forEach(order => {
        let ncrOrder = _.cloneDeep(order)
        ncrOrder.level = level
        ncrOrder.indentDisplay = _.repeat('&nbsp;', level*3) + (level > 0 ? '<i class="fas fa-caret-right"></i>&nbsp;' : '')
        sourceList.push(ncrOrder)

        this.buildNCROrderList(ncrOrder.childOrders, sourceList, level+1)
      })

      return sourceList
    },

    onTabShowDetails() {
      if(!this.itemList.loaded && !this.itemList.loading) {
        this.itemList.loading = true
        var orderComp = this
        this.$nextTick(() => {
          // Brief pause while spinner is displayed before loading OrderItemList component
          setTimeout(() => {
            orderComp.itemList.loading = false
            orderComp.itemList.loaded = true
          }, 100)
        })
      }

    }
  }
}

</script>